/* eslint-disable eqeqeq */
/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Phase 6 Vendor-Port W3 — ENERGY WHEEL SELECTOR (§33.3).
 *
 * React port of components/ewheel/view/energy_wheel.selector.js (3,365) + its
 * templates (ewheel.selector + ewheel.rated.outputs + pricing_method_inputs)
 * into the 6e2c component-selector chassis (§30) as the 'energy_wheel'/'er_wheel'
 * tab body. The FIRST external-vendor selector: Rate fires POST /api/v1.0/rating/
 * {part:'erwheel'} → Annexair/Innergy/Heatex/AI-Rotor, via the SHARED
 * useRatingDispatch. Dual-section (the EW spans both tunnels); board + header
 * (header deferred — board only here, like W1/W2).
 *
 * Opened via the EW FORM's rating label: EnergyWheelForm's FormHeading
 * (withRatingBlock, ratingBlockDataName='energy_wheel') → openRatingStub →
 * rating_mode:activate {name: 'er_wheel'(partName), current_model:{id}, section}.
 * The §30.4 fan-out key is therefore `<activate.name>_<id>:selector:<action>` —
 * we subscribe to the name we were opened under (Q22: er_wheel | energy_wheel |
 * rotary all route here via the registry aliases).
 *
 * ★ DATA-SAFETY (§4 + the 6e2a userArmed gate — HARD REQUIREMENT):
 *   - RATE fires an EXTERNAL vendor call (POST /rating/). SELECT writes
 *     (current_model.set → auto-save PATCH ahu/energy_wheels/<id> + the
 *     energy_wheel_params blob). BOTH are armed ONLY by a real #rate/#select
 *     click (userArmed ref). NOTHING fires on mount/open/tab-switch/browse.
 *   - SMOKE = open / render / cancel → 0 mutating calls, 0 /rating/ calls.
 *   - The legacy view fires NO write on init either (it only renders the saved
 *     model). We render the STORED rated state read-only.
 *
 * THE DEPENDENCY ENGINE (the W3 deliverable wired here): useDependencyGraph runs
 * the legacy DependenciesHandler cascade (parent change → descendant
 * reset/reload, layered topological) + the VersionedDataRegistry last-write-wins.
 * Option lists resolve through:
 *   - the er_wheel CMS doc (mfg/series/mounting_arr/mtr_voltage — energyWheelCms,
 *     which reuses uvcCms.getFromCmsData), and
 *   - the master/cms param lists (everything else — ewParamUrl / getMasterListByUrl).
 * Read-only, the saved value always renders (the option list is the full CMS
 * superset). The live re-cascade fires only when the user changes a parent — and
 * because Rate/Select are userArmed, the read-only smoke never mutates.
 */

import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { ahuBoardBus } from '../../bus/ahuBoardBus';
import {
  RATING_STORAGE_KEYS,
  readRatingMap,
  findLastEntry,
  findEntryByMfg,
  writeRatingEntry,
  type IndependentRatingEntry,
  type IndependentRatingMap,
} from 'pages/dashboard/components/header/independentRatingStorage';
import { optionLabel, type OptionRow } from '../formKit';
import { formatDecimalDisplay, formatIntegerDisplay } from '../formUtils';
import { useAppDispatch } from 'app/hooks';
import {
  ahuComponentFormsApi,
  useGetComponentDetailQuery,
  useGetComponentCmsDocQuery,
  useGetMasterListByUrlQuery,
} from 'app/services/ahuComponentFormsApi';
import { useGetAhuTunnelListQuery } from 'app/services/ahuBoardApi';
import { formatTunnelAddress } from '../../addressing';
import { EnergyWheelRatedOutputs } from './EnergyWheelRatedOutputs';
import { useRatingDispatch } from './useRatingDispatch';
import { buildRatingEnvelope } from './ratingEnvelope';
import { useDependencyGraph, type LoadParamsFn } from './useDependencyGraph';
import {
  EW_CMS_DOC_URL,
  EW_NOTES_URL,
  EW_FLOW_TYPES_URL,
  EW_FAN_LOCATIONS_URL,
  EW_AIR_STREAMS_URL,
  EW_PRICING_METHODS_URL,
  EW_DEPENDENCY_GRAPH,
  EW_DEPENDENCY_GRAPH_HEADER,
  EW_REQUIRED_FIELDS,
  EW_DEFAULTS,
  EW_MFG_DEFAULTS,
  EW_PURGE_SECTOR_DEFAULTS,
  EW_SERIES_DEFAULTS,
  WBRH_TOGGLE_MFG_IDS,
  WBRH_TOGGLE_DEFAULTS,
  SUMMER_REQUIRED_FIELDS_FOR_RATE,
  WINTER_REQUIRED_FIELDS_FOR_RATE,
  PARAMETER_CMS_PATHS_MAPPING,
  PARAMETER_NAMES,
  ewParamUrl,
  EW_TYPE_ENUM,
  EW_FLOW_TYPE_ENUM,
  EW_FIN_PATTERN_ENUM,
  ewRateEnumValue,
} from './energyWheelSelectorData';
import {
  ewCmsOptionList,
  ewCmsSelectedLabel,
  type EnergyWheelCmsDoc,
} from './energyWheelCms';
import { useGetEnergyWheelNotesQuery } from './energyWheelSelectorApi';
import type { ComponentFormShim } from '../componentFormTypes';
import type { SelectorTabProps } from './selectorTypes';
import { guardSelect } from '../cascadeCommit';
import { pendingSelectWrites, selectWriteKey, trackSelectWrites } from './selectWriteGate';
import { disableSyncIfAirFieldEdited } from '../commitAirConditions';
import { takeComponentPendingRerate } from '../../psychrometric/pendingRerate';
import { generateRatingTimestamp } from './ratingTimestamp';
import { sweepModelFieldInputs, mergeSweptFields } from './modelFieldSweep';

const num = (v: string): number | null => {
  if (v === '' || v === 'None' || v === 'none' || v === '--') return null;
  const n = Number(v.replace(/,/g, ''));
  return Number.isNaN(n) ? null : n;
};

/**
 * ★ RATE CONTRACT — the rate payload's numeric fields must be NUMBERS.
 *
 * Legacy only ever put numbers in `dataForRate`: `changeModelField` read these
 * boxes through the number-formatter widget (`e.target.getValue()`,
 * energy_wheel.selector.js:2385) and the initial render re-parsed every input
 * with `+value` (`isNaN(parsed) ? value.trim() : parsed`, js:1564-1566).
 *
 * React deliberately keeps a model field's RAW string in `state` so `PerfNum`
 * can be typed in freely (`freeTypingProps` would fight a mid-typing "6." → 6),
 * and that string reached the wire: `"oa_db_temp_winter": "65"` where legacy
 * sent `65`. innergy_tech reads the un-mapped input back when it builds the
 * AHRI certification note and compares it to a float, so the whole rate died
 * with `'>=' not supported between instances of 'str' and 'float'`
 * (innergy_tech/components/business_processor.py:402).
 *
 * Coerce at the payload boundary — the legacy `collectData()` position — so the
 * free-typing inputs are untouched. Non-numeric sentinels ('--') and nulls pass
 * through unchanged, because the blank-season deletion below keys off them.
 */
const EW_RATE_NUMERIC_FIELDS = [
  'oa_sp', 'ra_sp',
  'difference_in_pressure_summer', 'difference_in_pressure_winter',
  ...(['summer', 'winter'] as const).flatMap((sea) =>
    (['oa', 'ra'] as const).flatMap((pre) => [
      `${pre}_airflow_actual_${sea}`,
      `${pre}_db_temp_${sea}`,
      `${pre}_wb_temp_${sea}`,
      `${pre}_rh_${sea}`,
    ])
  ),
];

const rateNum = (v: unknown): unknown => {
  if (typeof v !== 'string') return v;
  const t = v.trim();
  if (t === '') return null;
  const n = Number(t.replace(/,/g, ''));
  return Number.isNaN(n) ? v : n;
};

function parseParams(raw: unknown): Record<string, unknown> {
  if (raw == null) return {};
  try {
    return JSON.parse(String(raw)) as Record<string, unknown>;
  } catch {
    return {};
  }
}

type SeasonTab = 'summer' | 'winter';
// Selector parity — restore legacy's SIX input sub-tabs (ewheel.selector.tmpl:6-24:
// Heat Exchanger [Properties] · Performance · Configuration · Mounting · Options ·
// Pricing). React had folded Configuration/Mounting/Options into Properties.
type InputTab =
  | 'Properties'
  | 'Performance'
  | 'Configuration'
  | 'Mounting'
  | 'Options'
  | 'Pricing';

/**
 * The selects legacy gives an explicit `<option value="None">--</option>`
 * placeholder (ewheel.selector.tmpl — every `has_default` select). React was
 * rendering a blank placeholder everywhere, so these read as an empty line
 * instead of the dash. Fields absent from this set have NO placeholder in
 * legacy (mfg/series/type/desiccant/fin_pattern/fin_thickness/speed/
 * speed_control/drive_belt/mtr_voltage/certification/flow_type/pricing_method).
 */
const EW_DASH_DEFAULT_FIELDS = new Set<string>([
  'wheel_size_id', 'rotor_style_id', 'rotor_depth_id', 'hub_id', 'seal_id',
  'well_height_id', 'casing_id', 'casing_type_id',
  'configuration_id', 'purge_sector_location_id',
  'fan_locations_id', 'mounting_arr_id', 'instl_position_id',
  'temp_control_id', 'rotation_detector_id', 'coating_frame_id',
  'coating_media_id', 'media_edge_id', 'mtr_service_door_id', 'bottom_plate_id',
]);

/**
 * The selects whose `--` stays in the list once a real value is picked, i.e.
 * where `--` is a VALUE the user can choose and not just an empty placeholder.
 *
 * Every other `EW_DASH_DEFAULT_FIELDS` select renders its dash only while the
 * field is null, so picking a row is one-way. Rotor Style is now `--` by
 * default for every manufacturer and `--` means "do not filter the catalogue by
 * rotor style" (#box-anna, 2026-10-06) — so the user must be able to get back to
 * it after trying a style. Legacy's own `has_default` selects keep the dash
 * permanently (the `<option value="None">--</option>` is in the TEMPLATE, ahead
 * of the rendered rows, ewheel.selector.tmpl:113); only this field is moved onto
 * that behaviour here, so no other dropdown changes.
 */
const EW_PERSISTENT_DASH_FIELDS = new Set<string>(['rotor_style_id']);

/** does `name`'s `--` option render right now? (always for the persistent ones) */
const ewShowsDash = (name: string, valStr: string): boolean =>
  EW_PERSISTENT_DASH_FIELDS.has(name) || valStr === '';

interface EWModel extends Record<string, unknown> {
  id?: number;
  master_energy_wheel_id?: number | null;
  energy_wheel_params?: string | null;
  generic_mfg?: boolean;
}

/**
 * ★ Phase 6.5 — the HEADER-mode default-state seed.
 *
 * Legacy synthesises a `current_model` out of `self.default_values`
 * (energy_wheel.selector.js:332-449) — `if (!self.current_model) { … new
 * Backbone.Model(self.default_values) … }` at :459-462 — and then, when NO
 * saved-storage entry exists, applies the `!self.defaults_already_exist` state
 * block at :1022-1046:
 *   min_face_velocity 200 · max_face_velocity 800 · oa_sp 0 · ra_sp 0 ·
 *   mfg_id = the mfg-233 (AI Rotor) row, else the first CMS mfg ·
 *   ...self.model (= the synthesized default_values) · wheel_size_id undefined ·
 *   then, when there is no master_energy_wheel_id:
 *   difference_in_pressure_winter/summer 1.5 · rotor_depth_id 8.
 * `EW_DEFAULTS` already carries every one of those constants, so the header seed
 * is `EW_DEFAULTS` + the per-season/`prefix` nulls from `default_values` +
 * `mfg_id: 233`.
 *
 * ★ The two AHU-derived branches in that block DO NOT run in header mode:
 * `...Object.keys(self.current_model).length && !self.header ? … : {}` (:1035 —
 * the catalogue results_filter) and the `if (self.ahu && self.ahu.get(...))`
 * mtr_voltage_id seed (:1042-1044; `self.ahu` is undefined there).
 */
const HEADER_EW_DEFAULTS: Record<string, unknown> = {
  ...EW_DEFAULTS,
  // (`oa_sp`/`ra_sp` deliberately stay at EW_DEFAULTS' 0.0. A static reading of
  // :1024-1030 suggests `...self.model` — the synthesized `default_values`, which
  // carries `"oa_sp": null` / `"ra_sp": null` at js:336-337 — should win over the
  // block's `oa_sp: 0.00, ra_sp: 0.00`. It does NOT: a first-ever legacy open on
  // /sizer/ renders BOTH inputs as "0.00". Measured, not inferred; a null seed was
  // tried and reverted.)
  // ★ 6.5 builder-3 — two fields legacy fills from the dependency handler at
  // construction rather than from `default_values`: it seeds their possible
  // values directly (`parameterPossibleIDs.updateData('mtr_voltage_id', …)` /
  // `('fan_locations_id', …)`, js:1079-1086), which runs applyParameterNewValues
  // with the graph's `default_values` — [1] for both (js:836-841 mtr_voltage_id
  // "460/3/60", js:799-803 fan_locations_id "eadt_oadt") — and REQUIRED_FIELDS
  // [233] lists both, so the setDefaultsIfNullChecker gate passes. Neither field
  // is ever cascaded (no parents), so the value is a constant for the whole
  // session, exactly as here. Confirmed live: "460/3/60" and
  // "Draw-Through OA, Draw-Through EA".
  mtr_voltage_id: 1,
  fan_locations_id: 1,
  // energy_wheel.selector.js:1029 — `(manufacturer.find((e) => e.id === 233) ||
  // manufacturer[0])?.id`. 233 = AI Rotor; on dev3 it is always present in the
  // er_wheel CMS `manufacturer` list, so the literal is the observed value.
  mfg_id: 233,
  // default_values members the flat React state also renders (js:333-336).
  energy_wheel_params: '{"series_id": 1306}',
  height_from_floor: 0,
  // (`fan_locations_id: null` — js:334 — is deliberately NOT repeated here: the
  // dependency handler immediately replaces it with 1, see the note above.)
  speed: null,
  select_mode_id: 2,
  generic_mfg: false,
  // js:1032 `wheel_size_id: undefined` — the ONLY member the block clears.
  wheel_size_id: undefined,
};

/**
 * ★ Phase 6.5 (builder 3) — doc-resolved EW fields loaded NARROWED from the
 * CMS-params endpoint instead of the full doc list, because legacy does exactly
 * that (see the note in `loadParams`). Header-only when it was written;
 * BOTH HOSTS as of 2026-09-03 — legacy's loadParams has no host branch.
 */
const EW_NARROWED_DOC_FIELDS = new Set<string>(['series_id', 'mounting_arr_id']);

/**
 * Selector parity — RESET's blank list (energy_wheel.selector.js:1826-1834).
 *
 * The heat wheel's `reset:` is NOT "restore a defaults blob" the way coil's and
 * the gas heater's are. Legacy walks its OWN rendered DOM
 *   `self.$el.find('.search_field').each(… if (elem.tagName == 'SELECT') …)`
 * and writes `null` for every matching element's `name`. This is that walk done
 * statically, from the template rather than the live DOM (React has no
 * equivalent "every rendered .search_field" query that is stable across the
 * per-mfg conditional rows — and legacy's own walk is mfg-independent because
 * `showHideInputsDependsOnMfg` only toggles a `hide` CLASS, so the rows it hides
 * are still in `$el` and still get blanked).
 *
 * Source: the 28 `<select class="search_field">` of ewheel.selector.tmpl at
 * lines 43, 57, 72, 85, 98, 113, 129, 144, 159, 188, 203, 217, 231, 246, 274,
 * 301, 885, 904, 922, 979, 1009, 1049, 1065, 1080, 1096, 1111, 1127, 1143.
 *
 * Deliberately NOT in this list:
 *  - the four `search_field` INPUTS — `eatr_oacf` (tmpl:574 + :832),
 *    `min_face_velocity` (:589), `max_face_velocity` (:594). Legacy's
 *    `elem.tagName == 'SELECT'` guard skips them, so they survive a reset.
 *  - `certification_id` (:173), `speed` (:261), `drive_belt_id` (:287): legacy
 *    renders those three as `model_field` selects, so `.search_field` never
 *    matches them. React happens to render them through `MasterSelect`, which
 *    stamps `className="search_field"` — a literal `querySelectorAll` port of
 *    legacy would therefore have blanked three fields legacy keeps. The legacy
 *    TEMPLATE is the authority here, not React's class names.
 *
 * `mfg_id` IS in the list because legacy nulls it too (js:1832) and then writes
 * it straight back from the restored `<option>` + `trigger('change')`; see
 * `doReset`, which preserves it in the same object write.
 */
const EW_RESET_SEARCH_SELECTS: readonly string[] = [
  'mfg_id',              // tmpl:43
  'series_id',           // tmpl:57
  'type_id',             // tmpl:72
  'desiccant_id',        // tmpl:85
  'wheel_size_id',       // tmpl:98
  'rotor_style_id',      // tmpl:113
  'rotor_depth_id',      // tmpl:129
  'hub_id',              // tmpl:144
  'seal_id',             // tmpl:159
  'fin_pattern_id',      // tmpl:188
  'fin_thickness_id',    // tmpl:203
  'well_height_id',      // tmpl:217
  'casing_id',           // tmpl:231
  'casing_type_id',      // tmpl:246
  'speed_control_id',    // tmpl:274
  'mtr_voltage_id',      // tmpl:301
  'configuration_id',    // tmpl:885
  'purge_sector_location_id', // tmpl:904
  'purge_angle_id',      // tmpl:922
  'mounting_arr_id',     // tmpl:979
  'instl_position_id',   // tmpl:1009
  'temp_control_id',     // tmpl:1049
  'rotation_detector_id',// tmpl:1065
  'coating_frame_id',    // tmpl:1080
  'coating_media_id',    // tmpl:1096
  'media_edge_id',       // tmpl:1111
  'mtr_service_door_id', // tmpl:1127
  'bottom_plate_id',     // tmpl:1143
];

/**
 * ★ FOCUS PARITY — why every sub-view of this selector lives at MODULE scope.
 *
 * `DocSelect`, `MasterSelect`, `PerfNum`, `SeasonBlock` … were all declared
 * INSIDE the selector's render, so each render produced a NEW function identity.
 * React keys reconciliation on element TYPE, so it saw a different type every
 * time and unmounted/remounted every `<input>` and `<select>` in the dialog on
 * any state change.
 *
 * That is what broke Tab. A numeric input commits on blur (`freeTypingProps` →
 * `onModelField`/`onSearchField` → `bump()`): pressing Tab fires blur, the
 * browser moves focus to the next input, and the re-render that follows then
 * DESTROYS that freshly-focused node — focus falls back to `<body>` and the
 * keystroke looks ignored. Tabbing WITHOUT editing worked (no commit, no
 * re-render), which is exactly how it was reported: "after entering a number,
 * Tab does not move to the next input". Changing a `<select>` dropped focus the
 * same way.
 *
 * Legacy cannot hit this: the pane is one Handlebars render and
 * `formatter.js:189-193` only rewrites the element's own `.value` on change, so
 * the node the browser just focused is still there. Hoisting these out restores
 * that — React now updates the existing DOM nodes in place and focus survives.
 *
 * They close over a lot of render state, so it travels through this context
 * rather than ~40 threaded props. A changing context VALUE re-renders consumers,
 * which is harmless; only a changing component IDENTITY remounts them.
 */
type EwViewCtx = {
  s: Record<string, unknown>;
  mfg: number;
  season: SeasonTab;
  isWbTemp: boolean;
  isHeatexE: boolean;
  isAnnexair: boolean;
  streams: OptionRow[];
  masterLists: Record<string, OptionRow[]>;
  ewDoc: unknown;
  /** the dependency graph's narrowed option ids for a field, null until loaded. */
  optionIds: (field: string) => Array<number | string> | null;
  onSearchField: (name: string, raw: string) => void;
  onModelField: (name: string, raw: string) => void;
  onCheckboxField: (name: string, checked: boolean) => void;
  onWbRhToggle: () => void;
};
const EwCtx = createContext<EwViewCtx | null>(null);
const useEw = (): EwViewCtx => {
  const ctx = useContext(EwCtx);
  if (!ctx) throw new Error('EnergyWheelSelector sub-view rendered outside EwCtx');
  return ctx;
};

export const EnergyWheelSelector = ({
  modelId,
  ahu,
  activate,
  setSelectEnabled,
  headerMode = false,
  eventScope,
  bus: busProp,
}: SelectorTabProps) => {
  // the chassis fan-out prefix = the name we were opened under (Q22 alias).
  const eventName = (activate.name as string) || 'energy_wheel';
  // §30.4 fan-out namespace: `energy_wheel_<id>` on the board, `energy_wheel_header`
  // in the header popup — legacy resolves the same fallback at
  // energy_wheel.selector.js:993 `self.model?.id ? self.model.id : "header"`
  // (`self.model = self.current_model?.toJSON()`, and the SYNTHESIZED header
  // current_model has no `id`).
  const scope = eventScope ?? String(modelId);
  // Isolated bus in header mode so the UN-namespaced emits below
  // (rating_mode:mounting:changed, cancel:dialog:changes …) can never reach a
  // board rating dialog open on the same page — discovery §7.3 trap 1.
  const bus = busProp ?? ahuBoardBus;
  // Own-subtree root for every DOM read. Legacy reaches `#select` with
  // DOCUMENT-WIDE jQuery selectors (energy_wheel.selector.js:2268, 2334, 2658,
  // 2903, 2905, 3261 `$('.control-buttons button[id="select"]')`) — on a page
  // hosting BOTH a header popup and a board dialog those cross-contaminate.
  // Discovery §7.3 trap 1.
  const rootRef = useRef<HTMLDivElement | null>(null);
  const chassisEl = useCallback(
    (): HTMLElement | null => rootRef.current?.closest('.ahu-rate-content') ?? null,
    []
  );
  // ewheel.selector.tmpl:6-31 `{{prefix}}Properties` etc. with
  // `prefix: self.header ? '' : 'ahu-'` (energy_wheel.selector.js:2604 uses the
  // same expression for activeOutputTabId). NOTE the existing board ids use an
  // UNDERSCORE (`ahu_Properties`) rather than legacy's hyphen — a pre-existing
  // 6e2c deviation left untouched; only the header (empty-prefix) case is added,
  // which is what keeps the header panes from colliding with a board dialog's.
  // legacy `prefix = self.header ? '' : 'ahu-'` (energy_wheel.selector.js:1527).
  // React had `ahu_` (underscore), so every legacy rule keyed on the board-mode
  // pane id missed — `#ahu-Performance select{width:69%}` (common.css:19457),
  // `#ahu-Notes-output` (:14988/:17336) and the report sheet's `#ahu-Performance`
  // block (report/styles.css:6052-6075).
  const idPrefix = headerMode ? '' : 'ahu-';

  // ---- read-only loads (fire on mount; never mutate) ----
  // ★ HEADER MODE: there is no current_model to read — legacy SYNTHESIZES one
  // from `self.default_values` (energy_wheel.selector.js:459-479) and issues no
  // component GET at all. `modelId` is 0 there; `skip` mirrors that exactly.
  const {
    data: detail,
    isLoading: detailLoading,
    isError: detailError,
    fulfilledTimeStamp: detailFulfilledAt,
  } = useGetComponentDetailQuery(
    { plural: 'energy_wheels', id: modelId },
    { refetchOnMountOrArgChange: true, skip: !modelId }
  );

  // ---- ★ seed only from a row that POSTDATES this open (the reopen gate) ----
  // Reported: rate a wheel, Select a model, reopen the Rating dialog → the whole
  // Output pane is missing. The row is saved (`master_energy_wheel_id` and
  // `rated_datetime` are both in the database), so this is a READ bug, and it is
  // the same one already fixed on the coil (CoilSelector.tsx:652-707) and the
  // evap pack (EvapPackSelector.tsx:174-212):
  //
  //   RTK Query keeps `getComponentDetail`'s unsubscribed entry for its default
  //   60 s (its only tag is invalidated by the cascade refresh, nothing else),
  //   while the chassis mounts a FRESH selector on
  //   every open (`key={dialog.openSeq}`). So a reopen inside that minute is
  //   served the CACHED — pre-Select — row synchronously.
  //   `refetchOnMountOrArgChange` does re-pull, but `isLoading` is false the
  //   whole time (it is true only on the very first ever load) and the seed
  //   below latches `seeded.current = modelId`, which blocks the re-seed when
  //   the fresh row lands. The stored-row render is gated on
  //   `m.master_energy_wheel_id`, so the pane renders NOTHING.
  //
  // Legacy cannot have this: `render_outputs` reads `self.current_model`
  // (energy_wheel.selector.js:597/:2884), the in-memory Backbone model that
  // Select already `.set()` on the way past — no re-read, nothing to be stale.
  //
  // ⚠ `isFetching` is not a usable gate: RTK Query starts the refetch from an
  // effect inside the hook, so the first render after mount still reports
  // `isFetching: false` next to the stale `data` — the seed effect runs in that
  // very commit. `fulfilledTimeStamp` is stamped when a request FULFILS, so a
  // row cached before this dialog opened can never satisfy `>= openedAt`.
  const openedAt = useRef(Date.now());
  // …and a GET issued while the previous Select is still saving would fulfil
  // after `openedAt` while STILL returning the pre-select row — the dialog closes
  // on the Select CLICK (finishSelect below runs before the POST resolves), so
  // that window is exactly the one the reporter clicks in. When this open follows
  // a Select whose chain is in flight, hold the gate open (`Infinity`) until
  // those writes settle, then re-read and gate on THAT moment.
  const pendingWrites = useRef(pendingSelectWrites(selectWriteKey('energy_wheels', modelId)));
  const [gateAt, setGateAt] = useState<number>(() =>
    pendingWrites.current ? Number.POSITIVE_INFINITY : openedAt.current
  );
  const dispatch = useAppDispatch();
  useEffect(() => {
    const writes = pendingWrites.current;
    if (!writes) return;
    let alive = true;
    void writes.then(() => {
      if (!alive) return;
      // stamped BEFORE the refetch is dispatched, so only its (later)
      // fulfilment can satisfy the gate.
      const at = Date.now();
      void dispatch(
        ahuComponentFormsApi.endpoints.getComponentDetail.initiate(
          { plural: 'energy_wheels', id: modelId },
          { forceRefetch: true, subscribe: false }
        )
      );
      setGateAt(at);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId]);
  // `isError` is the escape hatch: a failed read seeds from whatever is cached
  // rather than pinning the preloader on for ever.
  const detailFresh = detailError || (detailFulfilledAt != null && detailFulfilledAt >= gateAt);
  // the er_wheel CMS doc (mfg/series/mounting_arr/mtr_voltage labels).
  const { data: ewDoc, isLoading: ewDocLoading } =
    useGetComponentCmsDocQuery(EW_CMS_DOC_URL);

  const airStreamsQ = useGetMasterListByUrlQuery(EW_AIR_STREAMS_URL);
  const flowTypesQ = useGetMasterListByUrlQuery(EW_FLOW_TYPES_URL);
  const fanLocationsQ = useGetMasterListByUrlQuery(EW_FAN_LOCATIONS_URL);
  // Selector parity — the REAL pricing_methods collection (legacy
  // self.pricing_methods, energy_wheel.selector.js:320/1013-1020/1531; global
  // master route, mfg-independent). React had hardcoded List/User.
  const pricingMethodsQ = useGetMasterListByUrlQuery(EW_PRICING_METHODS_URL);
  const notesQ = useGetEnergyWheelNotesQuery(EW_NOTES_URL);
  // The AHU's tunnels — only to resolve a section's tunnel INDEX NAME ("1:1",
  // "2:1") for the Available Airway rows and the required-airway output column.
  // Legacy gets it off the section's own Backbone tunnel
  // (`s.tunnel.get_index().name`, energy_wheel.selector.js:495); the React
  // section carries only `tunnel_id`, so the name comes from the tunnel list
  // (`index`, AHUTunnelSerializer:45 `source='index.name'`). Header mode has no
  // AHU at all — skip, and the rows collapse exactly like legacy's empty
  // `current_model.view.sections`.
  const tunnelsQ = useGetAhuTunnelListQuery(ahu?.id as number, {
    skip: headerMode || ahu?.id == null,
  });

  // ---- state model (legacy state Backbone model) ----
  const state = useRef<Record<string, unknown>>({});
  const init = useRef<Record<string, unknown>>({});
  const seeded = useRef<number | null>(null);
  const [, setVersion] = useState(0);
  const bump = useCallback(() => setVersion((v) => v + 1), []);
  /**
   * ★ Bumped by BOTH seed effects (header + board) on the commit that latches
   * `seeded.current = modelId`. Every post-seed effect that must fire exactly
   * once, on that commit, keys on this — never on `detail` alone.
   *
   * Why: with the freshness gate above, the seed no longer lands on the commit
   * `detail` first arrives. When the mount refetch comes back deep-equal to the
   * cached row, RTK Query's structuralSharing hands back the SAME object
   * reference, so a `[detail]` dep list never changes after mount — the effect
   * would run once, bail on `seeded.current` (still null, refetch in flight) and
   * never run again. That is the trap the coil hit (CoilSelector.tsx:876-905)
   * and it silently empties every dependency-driven option list.
   */
  const [seedTick, setSeedTick] = useState(0);
  const markSeeded = useCallback(() => setSeedTick((t) => t + 1), []);

  // userArmed gate (arms ONLY on a real #rate/#select click).
  const userArmed = useRef(false);
  const { dispatchRating, abortRating, isRating } = useRatingDispatch(
    headerMode ? 'header' : 'board'
  );

  const [activeTab, setActiveTab] = useState<InputTab>('Properties');
  // legacy opens on WINTER: the nav lists winter first with `active`, and the
  // `#winter` pane carries `fade in active` (ewheel.selector.tmpl:353-354/:617).
  const [season, setSeason] = useState<SeasonTab>('winter');
  // WB-vs-Humidity toggle (legacy isWbTemp).
  const [isWbTemp, setIsWbTemp] = useState(true);

  // the rated models from a fresh Rate (read-only: the saved model renders as
  // the single stored row).
  const [ratedRows, setRatedRows] = useState<Array<Record<string, unknown>> | null>(null);
  const [ratedIndex, setRatedIndex] = useState(0);
  // Render-time mirrors so the header-mode `saveInStorage` (legacy reads the
  // synchronous `self.rated_erwheels` / `self.rated_erwheel_index`) has a default
  // source; call sites that mutate them in the SAME tick pass explicit overrides.
  const ratedRowsRef = useRef<Array<Record<string, unknown>> | null>(null);
  ratedRowsRef.current = ratedRows;
  const ratedIndexRef = useRef(0);
  ratedIndexRef.current = ratedIndex;
  const [rateErrors, setRateErrors] = useState<string[]>([]);
  const [rateWarnings, setRateWarnings] = useState<string[]>([]);
  const ratedDatetime = useRef<string | null>(null);

  // mfg-master param lists from the dependency graph (field → option-id list).
  // The dependency engine narrows these; read-only we seed full lists.
  const [masterLists, setMasterLists] = useState<Record<string, OptionRow[]>>({});
  const masterListsRef = useRef<Record<string, OptionRow[]>>({});
  masterListsRef.current = masterLists;

  // ---- ★ Phase 6.5 — HEADER-MODE STANDALONE SEED ---------------------------
  // energy_wheel.selector.js:459-483. There is no current_model, so legacy
  // SYNTHESIZES one and seeds from the `last:true` entry of
  // `localStorage.rotary_independent_ratings` (note the `rotary` key name):
  //   self.current_model = new Backbone.Model({...default_values, ...model.state,
  //                                            er_wheel_mfg: model.er_wheel_mfg})
  //   self.dataForRate = model.dataForRate; self.dataForSave = model.dataForSave;
  //   self.state = new Backbone.Model(model.state);
  //   self.needToRate = model.rate;  self.defaults_already_exist = true;
  //   self.saved_rated_erwheel_index = model.rated_erwheel_index;
  //   self.generic_mfg = model.generic_mfg;  self.mfg_id = model.mfg_id;
  //
  // ★★ RESOLUTION of builder 1's open question — "ewheel's saveInStorage calls
  // self.current_model.toJSON() and current_model is undefined in header mode":
  // it is NOT undefined. The header branch above CONSTRUCTS it
  // (`new Backbone.Model(self.default_values)` at :461 when there is no saved
  // entry, or the defaults+state merge at :464-468 when there is), so
  // `.toJSON()` returns the synthesized attributes and the write SUCCEEDS. The
  // ewheel entry therefore really does carry a `current_model` member. (The
  // `'header'` fallback in the CHASSIS — component_selector.js:280 — is about the
  // chassis's own `current_model`, which is a different object.)
  /** legacy `self.storage_saved_data`. */
  const storageMapRef = useRef<IndependentRatingMap>({});
  /** legacy `self.mfg_id` — the selected Manufacturer option's `data-id`
   * attribute (js:2304), which is what KEYS the entry (js:1389
   * `self.mfg_id || self.state.get('mfg_id')`). */
  const ewMfgIdRef = useRef<unknown>(null);
  /** legacy `self.mfg_name` — the option's TEXT (js:2305), persisted as
   * `er_wheel_mfg` (js:1399). */
  const ewMfgNameRef = useRef<string | null>(null);
  /** legacy `self.generic_mfg` (js:1793-1794 / the saved `generic_mfg`). */
  const genericMfgRef = useRef<unknown>(false);
  /** legacy `self.rated_erwheel_index` / `self.saved_rated_erwheel_index`. */
  const savedRatedIndex = useRef<number | null>(null);
  /** legacy `self.dataForSave = {modelField:{}, searchField:{}}` (js:321-324). */
  const dataForSaveRef = useRef<{ modelField: Record<string, unknown>; searchField: Record<string, unknown> }>(
    { modelField: {}, searchField: {} }
  );
  /** legacy `self.dataForRate` (js:327 `self.dataForRate || {}`). */
  const dataForRateRef = useRef<Record<string, unknown>>({});
  /** legacy `self.needToRate` (js:471) + `self.needToSearch` (js:2337), folded
   * into one pending slot; resolved from the cascade `finished` hook. */
  const pendingRestore = useRef<{
    entry: IndependentRatingEntry;
    applyState: boolean;
  } | null>(null);

  useEffect(() => {
    if (!headerMode) return;
    if (seeded.current === modelId) return;
    const map = readRatingMap(RATING_STORAGE_KEYS.energy_wheel);
    const entry = findLastEntry(map);
    const s: Record<string, unknown> = { ...HEADER_EW_DEFAULTS };
    if (entry) {
      // js:466 — `{...default_values, ...model.state}` is what lands in the
      // (synthesized) current_model, and `self.state = new
      // Backbone.Model(model.state)` (:470) makes `state` the live model. React
      // has ONE flat state, so the merge is the same object.
      Object.assign(s, (entry.state ?? {}) as Record<string, unknown>);
      ewMfgIdRef.current = entry.mfg_id ?? null;
      ewMfgNameRef.current = (entry.er_wheel_mfg as string | null) ?? null;
      genericMfgRef.current = entry.generic_mfg ?? false;
      savedRatedIndex.current = (entry.rated_erwheel_index as number | null) ?? null;
      dataForRateRef.current = (entry.dataForRate as Record<string, unknown>) ?? {};
      dataForSaveRef.current =
        (entry.dataForSave as { modelField: Record<string, unknown>; searchField: Record<string, unknown> }) ??
        { modelField: {}, searchField: {} };
      // js:471 `self.needToRate = model.rate` → the cascade `finished` hook
      // (js:943-946) re-fires the rate. Gated on `rate` — UNLIKE coil.
      if (entry.rate) pendingRestore.current = { entry, applyState: false };
    } else {
      ewMfgIdRef.current = s.mfg_id ?? null;
    }
    // Selector parity — Heatex-E auto-force (updateOacfControl, js:2418-2432).
    if (Number(s.mfg_id) === 52 && Number(s.series_id) === 274) s.eatr_oacf = true;
    storageMapRef.current = map;
    state.current = s;
    init.current = { ...s };
    seeded.current = modelId;
    markSeeded();
    // the WB/RH toggle initial state per mfg (legacy applyWbRhToggleState).
    const mfg = Number(s.mfg_id);
    if (WBRH_TOGGLE_MFG_IDS.has(mfg)) setIsWbTemp(WBRH_TOGGLE_DEFAULTS[mfg]);
    bump();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerMode, modelId]);

  /**
   * `saveInStorage` (energy_wheel.selector.js:1385-1402). Guarded exactly like
   * legacy: `if (!self.storage_saved_data || !self.header) return;`.
   *
   * ★ MEMBER ORDER is byte-parity with js:1389-1399 —
   *   state, rate, last, current_model, dataForRate, dataForSave,
   *   rated_erwheel_index, mfg_id, generic_mfg, er_wheel_mfg
   * (JSON.stringify preserves insertion order.)
   *
   * ★ `current_model` is the SYNTHESIZED model's toJSON (see the seed note).
   * Legacy's is `{...default_values, ...saved.state, er_wheel_mfg}`; the legacy
   * READER re-spreads its OWN `default_values` underneath it anyway (js:1334-1339
   * `{...self.default_values, ...model.current_model, ...model.state, …}`), so
   * emitting the live flat state (which already sits on HEADER_EW_DEFAULTS) is
   * byte-equivalent for every member either side reads.
   *
   * ★ `generic_mfg: self.generic_mfg || self.state.get('mfg_id')` (js:1398) —
   * a FALSY generic_mfg falls through to the manufacturer id, which is why the
   * reader compares `model.generic_mfg != model.mfg_id` (js:1361). Mirrored
   * verbatim; it is not a boolean. (Verified live: a first write emits
   * `generic_mfg: 233` alongside `mfg_id: 233`.)
   *
   * ★ ONE DELIBERATE, NON-OBSERVABLE BYTE DIFFERENCE: `er_wheel_mfg:
   * self.mfg_name` is `undefined` until the user picks a Manufacturer, and
   * JSON.stringify DROPS an undefined member; we emit `null` so the key is
   * present. Both are falsy everywhere the value is read
   * (`self.model.er_wheel_mfg` → the name comparison at js:3155), so no legacy
   * read path can tell them apart.
   */
  const saveInStorage = useCallback(
    (over?: { rate?: boolean; ratedIndex?: number | null }) => {
      if (!headerMode) return;
      const flat = JSON.parse(JSON.stringify(state.current)) as Record<string, unknown>;
      storageMapRef.current = writeRatingEntry(
        RATING_STORAGE_KEYS.energy_wheel,
        ewMfgIdRef.current ?? state.current.mfg_id,
        {
          state: flat,
          // legacy `rate: !!self.rated_erwheels?.[self.rated_erwheel_index || 0]`.
          rate:
            over?.rate ??
            Boolean(ratedRowsRef.current?.[ratedIndexRef.current || 0]),
          last: true,
          current_model: flat,
          dataForRate: JSON.parse(JSON.stringify(dataForRateRef.current)),
          dataForSave: JSON.parse(JSON.stringify(dataForSaveRef.current)),
          rated_erwheel_index:
            over && 'ratedIndex' in over ? (over.ratedIndex ?? 0) : ratedIndexRef.current,
          mfg_id: ewMfgIdRef.current ?? null,
          generic_mfg: genericMfgRef.current || state.current.mfg_id,
          er_wheel_mfg: ewMfgNameRef.current,
        } as unknown as IndependentRatingEntry,
        storageMapRef.current
      );
    },
    [headerMode]
  );

  // ---- seed state once GET resolves (BOARD host) ----
  useEffect(() => {
    if (headerMode) return;
    if (!detail) return;
    // ★ the reopen gate (see `detailFresh` above): never seed from a row that
    // predates this open — on a reopen after Select that row is the pre-Select
    // one and the Output pane renders empty.
    if (!detailFresh) return;
    if (seeded.current === modelId) return;
    const m = detail as EWModel;
    const p = parseParams(m.energy_wheel_params);
    const s: Record<string, unknown> = {
      ...EW_DEFAULTS,
      // params blob ids (the search-field selection).
      mfg_id: p.mfg_id,
      series_id: p.series_id,
      type_id: p.type_id,
      fin_pattern_id: p.fin_pattern_id,
      wheel_size_id: p.wheel_size_id,
      purge_angle_id: p.purge_angle_id,
      desiccant_id: p.desiccant_id,
      rotor_style_id: p.rotor_style_id,
      rotor_depth_id: p.rotor_depth_id ?? EW_DEFAULTS.rotor_depth_id,
      seal_id: p.seal_id,
      drive_belt_id: p.drive_belt_id,
      fin_thickness_id: p.fin_thickness_id,
      well_height_id: p.well_height_id,
      hub_id: p.hub_id,
      casing_id: p.casing_id,
      casing_type_id: p.casing_type_id,
      purge_sector_location_id: p.purge_sector_location_id,
      configuration_id: p.configuration_id,
      mounting_arr_id: p.mounting_arr_id,
      speed_control_id: p.speed_control_id,
      speed: p.speed,
      // ★ 2026-09-03 — legacy fills BOTH of these at construction and React
      // left them null on the board, so Motor Voltage and Fan Locations opened
      // blank on an unrated wheel:
      //  · `mtr_voltage_id` — energy_wheel.selector.js:1041-1044 seeds the AHU's
      //    own main voltage when the wheel carries no catalogue pick, and the
      //    unconditional `parameterPossibleIDs.updateData('mtr_voltage_id', …)`
      //    (:1074-1077) then applies the graph default [1] = 460/3/60 if it is
      //    still null. HEADER_EW_DEFAULTS already carries the same two rules
      //    (minus the AHU branch — there is no AHU in the header host).
      //  · `fan_locations_id` — :1082-1085 `updateData('fan_locations_id', …)`
      //    → graph default [1] (Draw-Through OA, Draw-Through EA).
      mtr_voltage_id:
        p.mtr_voltage_id ??
        (m.master_energy_wheel_id ? undefined : (ahu?.ahu_main_voltage_id as number | undefined)) ??
        1,
      temp_control_id: p.temp_control_id,
      rotation_detector_id: p.rotation_detector_id,
      coating_frame_id: p.coating_frame_id,
      coating_media_id: p.coating_media_id,
      media_edge_id: p.media_edge_id,
      mtr_service_door_id: p.mtr_service_door_id,
      bottom_plate_id: p.bottom_plate_id,
      // Selector parity — instl_position_id was FULLY DROPPED in React (never
      // seeded/rendered/saved → data loss). Restore the round-trip: seed from the
      // params blob (legacy state.set on the Mounting-tab select, tmpl:1009). It
      // is a legacy REQUIRED field (energy_wheel.selector.js:232) and already
      // reaches the vendor rate via the PARAMETER_NAMES loop (as `instl_position`).
      instl_position_id: p.instl_position_id,
      certification_id: p.certification_id,
      min_face_velocity: p.min_face_velocity ?? EW_DEFAULTS.min_face_velocity,
      max_face_velocity: p.max_face_velocity ?? EW_DEFAULTS.max_face_velocity,
      eatr_oacf: p.eatr_oacf,
      // model (perf) fields — both seasons × oa/sa/ra/ea.
      flow_type_id: m.flow_type_id ?? EW_DEFAULTS.flow_type_id,
      fan_locations_id: m.fan_locations_id ?? 1,
      oa_sp: m.oa_sp ?? EW_DEFAULTS.oa_sp,
      ra_sp: m.ra_sp ?? EW_DEFAULTS.ra_sp,
      pricing_method_id: m.pricing_method_id ?? EW_DEFAULTS.pricing_method_id,
      cost_product_user: m.cost_product_user,
      cost_freight_user: m.cost_freight_user,
      net_price_product_user: m.net_price_product_user,
      net_price_freight_user: m.net_price_freight_user,
      difference_in_pressure_summer:
        m.difference_in_pressure_summer ?? EW_DEFAULTS.difference_in_pressure_summer,
      difference_in_pressure_winter:
        m.difference_in_pressure_winter ?? EW_DEFAULTS.difference_in_pressure_winter,
      elevation: m.altitude ?? (m as any).elevation ?? 0,
    };
    // copy every per-season oa/sa/ra/ea model field straight off the saved row.
    for (const sea of ['summer', 'winter'] as const) {
      for (const pre of ['oa', 'sa', 'ra', 'ea'] as const) {
        for (const f of [
          'air_stream', 'airflow_actual', 'airflow_std', 'db_temp', 'wb_temp',
          'rh', 'hr', 'w', 'face_velocity', 'apd',
        ]) {
          const key = f === 'air_stream' ? `${pre}_air_stream_${sea}_id` : `${pre}_${f}_${sea}`;
          if (m[key] != null) s[key] = m[key];
          else if (EW_DEFAULTS[key] != null) s[key] = EW_DEFAULTS[key];
        }
      }
    }
    // Dev-sync J5 — the chart's pending re-rate handoff, COMPONENT-scoped
    // (the bridge appears on both airstreams — energy_wheel.selector.js:
    // 578-605): target==='component', component_id matches, section_id
    // matches EITHER of the wheel's two sections. ★ DELIBERATE LEGACY
    // BUG-FIX (survey §B5, ordered by the J5 brief): legacy's wheel stashes
    // `_pendingRerateOverride` and never reads it — the fresh entering
    // conditions never reached the rendered inputs. PHX's consuming
    // behavior (plate_heat_exchanger.selector.js:1578 — merge the override
    // onto the render data) is ported HERE instead: the fields merge into
    // the seed state, which IS this selector's render data.
    const pendingFields = takeComponentPendingRerate(modelId, [
      (m as Record<string, unknown>).section_id as number | undefined,
      (m as Record<string, unknown>).section_2_id as number | undefined,
    ]);
    if (pendingFields) {
      Object.assign(s, pendingFields);
      // legacy :584 clears the catalog pick so the already-rated gate reads
      // fresh; :603 lands on Performance.
      s.master_energy_wheel_id = undefined;
      setActiveTab('Performance');
    }
    // Selector parity — Heatex-E auto-force (updateOacfControl,
    // energy_wheel.selector.js:2418-2432): mfg 52 / series 274 forces EATR-OACF on
    // (and the checkbox disabled). Seed it so a saved Heatex-E unit opens checked.
    if (Number(s.mfg_id) === 52 && Number(s.series_id) === 274) s.eatr_oacf = true;
    state.current = s;
    init.current = { ...s };
    seeded.current = modelId;
    markSeeded();
    // the WB/RH toggle initial state per mfg (legacy applyWbRhToggleState).
    const mfg = Number(s.mfg_id);
    if (WBRH_TOGGLE_MFG_IDS.has(mfg)) setIsWbTemp(WBRH_TOGGLE_DEFAULTS[mfg]);
    // render the STORED rated row read-only (legacy render_outputs initial).
    // J5: not after a pending re-rate — the stored rating is stale against
    // the fresh entering (the PHX pattern blanks .output-part, phx:1603).
    if (m.master_energy_wheel_id && !pendingFields) {
      // ★ PARITY (energy_wheel.selector.js:2913-2921). This row is legacy's
      // `rated_model` — `er_wheels = self.rated_erwheels || [rated_model]`
      // (:2884), i.e. the ONE row the Heat Exchanger output table lists until a
      // rate replaces it — and legacy patches it before rendering, because SIZE
      // and DEPTH are not columns on the saved wheel: they live in the
      // `energy_wheel_params` blob ("state.wheel_size_id is kept undefined for
      // input dropdown", :2915). Without the patch dev3 drew both cells empty
      // where dev prints the blob's values, and — since `optimal-model` is stamped on
      // the smallest PARSEABLE `wheel_size` (:2896-2904) — the row also lost
      // that class, hence its label colour (#212121, common.css:19623).
      // `EnergyWheelRatedOutputs` carries the same fallback, but only on its
      // `rows.length ? rows : [...]` else-branch, which this state never takes.
      //
      // All three of legacy's fallbacks read `self.state`, and state is seeded
      // with `...results_filter.toJSON()` (:1080) — which IS the parsed
      // `energy_wheel_params` blob (`catalogue_select_field.js:165-167`
      // `results_filter.set(JSON.parse(model.get(paramsSourceField)))`). So
      // `state.rotor_depth` / `state.fin_pattern` are `p.rotor_depth` /
      // `p.fin_pattern` here; 'Standard' is only the last resort, exactly as
      // `state.get('fin_pattern') || 'Standard'` (:2920). Hardcoding the
      // literal would repaint FIN PATTERN "Standard" over a wheel whose blob
      // says "High Performance".
      const stored = { ...(m as Record<string, unknown>) };
      if (!stored.wheel_size) stored.wheel_size = p.wheel_size || p.wheel_size_id || s.wheel_size_id;
      if (!stored.rotor_depth) stored.rotor_depth = p.rotor_depth;
      if (!stored.fin_pattern) stored.fin_pattern = p.fin_pattern || 'Standard';
      setRatedRows([stored]);
      ratedDatetime.current = (m.rated_datetime as string) ?? null;
    }
    bump();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail, detailFresh, modelId, bump, headerMode]);

  /**
   * The unit's tunnel divider ('vertical' | 'horizontal' | null), legacy
   * `getDividerFromTunnel` (energy_wheel.selector.js:1453-1473). It gates BOTH
   * the Configuration option list (loadParams, js:1346-1367) and which diagrams
   * the picture strip shows (filterConfigurationImages, js:1475-1493). In header
   * mode there is no AHU, so it stays null and neither is narrowed — same as
   * legacy, whose `self.ahu` is unset there.
   */
  // ★ Read the `ahu` PROP, not `activate.ahu`. On the board the two are not the
  // same object: `rating_mode:activate` is emitted with `{name, loading,
  // current_model, section, section2}` and no `ahu` at all
  // (useComponentForm.ts:526-532); the real AHU arrives as the sibling prop
  // ComponentSelectorDialog passes at :553. `dividerFromTunnel(activate)` was
  // therefore ALWAYS null on the board — which also sent the rate envelope's
  // `tunnel_configuration` (js:2547) as null on every board rate, not just the
  // header. `activate.ahu` is kept as the fallback for any host that does carry
  // it.
  const tunnelConfigValue =
    (ahu?.tunnel_configuration_value as string | null | undefined) ??
    (activate.ahu as { tunnel_configuration_value?: string } | null | undefined)
      ?.tunnel_configuration_value;
  /**
   * ★ HYBRID (KA-5217) — a hybrid unit's `tunnel_configuration_value` is the
   * literal `'hybrid'` (models_unit.py:4013), so there is no "X_Y" for
   * `dividerFromTunnel` to parse and NOTHING narrowed: the Configuration
   * dropdown offered all 8 rows and the strip drew 8 diagrams on a unit legacy
   * would have called "1_2" and shown 4 of each.
   *
   * Legacy's "X_Y" IS `max(row)_max(level)` over the unit's tunnels
   * (`get_tunnel_configuration`, models_unit.py:4016), so comparing those two
   * extents off the tunnel list reproduces `getDividerFromTunnel` exactly —
   * including its "equal extents narrow nothing" case (1_1, 2_2) — and keeps
   * working on a hybrid unit, whose value carries no extents at all. It is only
   * consulted when the value itself yields nothing, so a plain unit still
   * narrows off the string it already has, before the tunnel list arrives.
   */
  const extentsDivider = useMemo(() => {
    const rows = tunnelsQ.data ?? [];
    if (!rows.length) return null;
    const maxOf = (key: 'row' | 'level') =>
      rows.reduce((acc, t) => Math.max(acc, Number(t[key]) || 1), 1);
    const maxRow = maxOf('row');
    const maxLevel = maxOf('level');
    if (maxLevel > maxRow) return 'horizontal';
    if (maxRow > maxLevel) return 'vertical';
    return null;
  }, [tunnelsQ.data]);
  const configDivider = useMemo(
    () => dividerFromTunnel(tunnelConfigValue) ?? extentsDivider,
    [tunnelConfigValue, extentsDivider]
  );

  /**
   * The divider sent as the rate envelope's `tunnel_configuration`, which the
   * backend uses for ONE thing: how to split the wheel's required airway across
   * the tunnels it spans (`get_required_aw_width` / `_height`,
   * erwheel/utils.py:26-48 — the only readers of the key).
   *
   * ★ A HYBRID unit has no "X_Y" to parse — `tunnel_configuration_value` is the
   * literal 'hybrid' (models_unit.py:4013) — which `configDivider` now covers
   * via `extentsDivider`. What is left here is the case BOTH of those call null:
   * equal extents (a 2_2 unit, 2 rows AND 2 levels), where the wheel still spans
   * tunnels and the backend must still split. Keep the #1803 rule for it — more
   * than one row means tunnels side by side (vertical divider), more than one
   * level means they are stacked (horizontal) — which is what the SAVED
   * component uses on the server side
   * (`AHUEnergyWheelSerializer.to_representation`). Without it the same wheel
   * reported a split pair when reopened and a single undivided number when
   * re-rated.
   *
   * Still separate from `configDivider` on purpose: narrowing the option list to
   * ONE divider is wrong for a unit that has both, but the airway split is not.
   */
  const rateDivider = useMemo(() => {
    if (configDivider) return configDivider;
    const rows = tunnelsQ.data ?? [];
    if (!rows.length) return configDivider;
    const maxOf = (key: 'row' | 'level') =>
      rows.reduce((acc, t) => Math.max(acc, Number(t[key]) || 1), 1);
    if (maxOf('row') > 1) return 'vertical';
    if (maxOf('level') > 1) return 'horizontal';
    return configDivider;
  }, [configDivider, tunnelsQ.data]);

  // ---- DEPENDENCY ENGINE (the W3 cascade) ----
  // loadParams resolves a field's option list from the er_wheel CMS doc (for
  // the doc-resolved fields) or the master/cms param branch. Read-only seeds the
  // full list; the engine narrows by parents on a parent change.
  const loadParams = useCallback<LoadParamsFn>(
    async (field, parents) => {
      // doc-resolved fields → the CMS doc full list (read-only superset).
      //
      // ★★ Phase 6.5 (builder 3) — EXCEPT `series_id` / `mounting_arr_id` in
      // HEADER mode. Legacy's `parameter_names` has series COMMENTED OUT
      // (energy_wheel.selector.js:552 `// series_id: 'series',`), so loadParams
      // sends it down the CMS-params branch and it comes back NARROWED BY
      // MANUFACTURER. Captured live from /sizer/ on a first-ever open:
      //   GET /cms/1/v1.0.0/ahu/energy_wheels/params/series_id/?mfg_id=233   → [1306]
      //   GET /cms/1/v1.0.0/ahu/energy_wheels/params/mounting_arr_id/?mfg_id=233&series_id=1306 → []
      //
      // ★ This is the ROOT CAUSE of the "worst hit" HEAT WHEEL tab. The graph's
      // series default_values are [828, 1306, 274] (js:657-661); against the FULL
      // doc list 828 (I4) matches first, and 828 is not a valid series for mfg
      // 233 — so EVERY child request went out as
      // `?mfg_id=233&series_id=828` and the server answered `[]`. That is why ~18
      // HEAT WHEEL selects rendered blank and `series_id` showed `I4` where
      // legacy shows `S`: with the narrowed list 828 is absent, the engine falls
      // through to 1306, and the whole cascade below it resolves.
      // (`mounting_arr_id`'s narrowed list is EMPTY, which is why legacy renders
      // it as `--` where the unnarrowed doc list showed "a".)
      //
      // The LABEL still comes from the doc (`ewCmsOptionLabel` field-aware:
      // series → row.series → "S"); only the id set changes.
      //
      // ★ 2026-09-03 — this is legacy's `loadParams`, which has NO header/board
      // branch (energy_wheel.selector.js:1195-1260): the narrowing applies in
      // BOTH hosts. It was gated on `headerMode` when 6.5 built it purely to
      // keep the board's request pattern unchanged; the board needs it for the
      // same reason the header did.
      if (field in PARAMETER_CMS_PATHS_MAPPING && !EW_NARROWED_DOC_FIELDS.has(field)) {
        return ewCmsOptionList(ewDoc as EnergyWheelCmsDoc, field).map((r) => ({
          ...r,
          id: r.id as number,
        }));
      }
      // master/cms param branch → fetch the narrowed list by the parent filters.
      let filters = parents
        .filter((pp) => state.current[pp] != null)
        .map((pp) => `${pp}=${state.current[pp]}`)
        .join('&');
      // ★ PARITY (energy_wheel.selector.js:1346-1367) — the Configuration list is
      // ALSO narrowed by the unit's tunnel divider. Every mfg/series carries all
      // 8 configurations (4 vertical: 1/4/5/8 · 4 horizontal: 2/3/6/7), and
      // `MasterAHUEnergyWheelConfigMfgSeriesViewSet` (rest_master/viewset.py:7400)
      // drops the ones whose divider does not match. The port never sent
      // `divider`, so the dropdown offered all 8 — you could pick a horizontal
      // configuration on a vertical unit, the default fell on id 1 (vertical) for
      // horizontal units, and the diagram strip beside it drew 8 tiles into a
      // pane capped at 360px (common.css:19448) instead of 4.
      if (field === 'configuration_id' && configDivider) {
        filters = filters ? `${filters}&divider=${configDivider}` : `divider=${configDivider}`;
      }
      const { url } = ewParamUrl(field, filters);
      try {
        const res = await fetch(url, {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!res.ok) return masterListsRef.current[field] ?? [];
        const body = await res.json();
        const rows: OptionRow[] = Array.isArray(body)
          ? body
          : ((body as any)?.results ?? []);
        setMasterLists((prev) => ({ ...prev, [field]: rows }));
        return rows.map((r: any) => ({ ...r, id: r.id ?? r.value ?? r }));
      } catch {
        return masterListsRef.current[field] ?? [];
      }
    },
    [ewDoc, headerMode, configDivider]
  );

  const getValue = useCallback((field: string) => state.current[field] as any, []);
  const setStateValue = useCallback(
    (field: string, value: any) => {
      state.current = { ...state.current, [field]: value };
      bump();
    },
    [bump]
  );
  const shouldSetDefaultIfNull = useCallback((field: string) => {
    // legacy setDefaultsIfNullChecker → true for REQUIRED fields (per the
    // current mfg's REQUIRED_FIELDS set).
    const req = EW_REQUIRED_FIELDS[Number(state.current.mfg_id)];
    return req ? req.has(field) : true;
  }, []);

  /**
   * RESET support — armed for the duration of a `#reset` cascade ONLY.
   *
   * `doReset` blanks every search-field select (legacy js:1826-1834) and lets
   * the mfg cascade refill them. Legacy refills from the per-leaf
   * `default_values` of its ONE dependency spec (js:662-920), gated by
   * `setDefaultsIfNullChecker` → the mfg's REQUIRED_FIELDS (js:982-991), which
   * `shouldSetDefaultIfNull` above already ports. The React BOARD spec,
   * however, deliberately carries no leaf `default_values` (6.5 builder-3 kept
   * `EW_DEPENDENCY_GRAPH` byte-identical so no board dialog's option-picking
   * would change; only `EW_DEPENDENCY_GRAPH_HEADER` transcribes them). With a
   * null value and no defaults, applyNewValues can only auto-pick a SOLE
   * option — so a board reset would blank ~24 selects and leave them blank,
   * which is neither legacy's behaviour nor a usable dialog.
   *
   * Rather than switch the board host onto the header spec (that would change
   * the ordinary mfg/series cascade for every board heat wheel, well outside a
   * reset fix), the legacy leaf defaults are supplied through `pickFallback`
   * and consulted ONLY while `resetCascade` is armed. Outside a reset this
   * callback returns `undefined` for every field, i.e. the engine behaves
   * exactly as it did before this change.
   */
  const resetCascade = useRef(false);
  const pickFallback = useCallback(
    (field: string, ids: Array<number | string>, current: number | string | null | undefined) => {
      // The graph node legacy MUTATES per manufacturer (updateDefaultsDependsOnMFG
      // js:2291-2292, and the same swap at open for mfg 237, js:1159-1160). The
      // React spec is a module constant, so the swap is expressed here instead —
      // and it is NOT gated on `resetCascade`, because legacy's is a property of
      // the live graph and applies to every cascade. It returns the module spec's
      // own `[6, 2, 1]` for every manufacturer but Annexair, so only 237 moves.
      if (field === 'purge_sector_location_id') {
        const byMfg = EW_PURGE_SECTOR_DEFAULTS(state.current.mfg_id as number | null);
        const pick = byMfg.find((d) => ids.some((e) => String(e) === String(d)));
        return pick === undefined ? undefined : pick;
      }
      // Annexair's `series_id`, the same shape and for the same reason: the graph
      // node carries no Annexair default and the narrowed list has two rows, so the
      // engine resolved `series_id` to null — permanently, the Series select being
      // hidden for Annexair. See EW_SERIES_DEFAULTS. Empty for every other
      // manufacturer ⇒ `undefined`, i.e. no opinion, so nothing else moves.
      if (field === 'series_id') {
        const byMfg = EW_SERIES_DEFAULTS(state.current.mfg_id as number | null);
        const pick = byMfg.find((d) => ids.some((e) => String(e) === String(d)));
        if (pick !== undefined) return pick;
      }
      // Rotor Style defaults to `--` for EVERY manufacturer (#box-anna,
      // 2026-10-06). Emptying its `default_values` is not enough on its own:
      // `applyNewValues` auto-picks a SOLE surviving option, so a manufacturer
      // that offers one style would still fill the field — and a reset would
      // refill it from the leaf defaults below. Returning null instead means the
      // engine clears an out-of-range value and leaves a null one alone (its
      // `next === current` guard), so the dash survives every cascade and every
      // reset until the user picks a style themselves.
      if (field === 'rotor_style_id') return null;
      if (!resetCascade.current || current != null) return undefined;
      const defs = EW_DEPENDENCY_GRAPH_HEADER[field]?.default_values ?? [];
      // legacy: `fallback_value.find(e => possible_values.has(e))`
      // (dependencies_handler.js:319) — first default that survives narrowing.
      const hit = defs.find((d) => ids.some((e) => String(e) === String(d)));
      // `undefined` defers to the built-in sole-option/null rule, which is the
      // rest of applyParameterNewValues (:321-323).
      return hit === undefined ? undefined : hit;
    },
    []
  );

  const graph = useDependencyGraph({
    // ★ 6.5 builder-3 introduced the leaf-`default_values` spec for the header
    // host only, to keep the board's option-picking byte-identical. 2026-09-03:
    // the board needs it too — legacy builds ONE DependenciesHandler from ONE
    // graph literal (energy_wheel.selector.js:606-927) and never branches on
    // `self.header`, so both hosts pick from the same leaf defaults.
    spec: EW_DEPENDENCY_GRAPH_HEADER,
    loadParams,
    getValue,
    setValue: setStateValue,
    shouldSetDefaultIfNull,
    pickFallback,
  });

  // ★ Phase 6.5 — mount cascade. Legacy constructs the DependenciesHandler and
  // runs the FULL parameter load on construction (36 GETs observed live on the
  // first HEAT WHEEL tab visit — discovery §2), so the ~20 master-list selects
  // render LABELS rather than raw ids, and the cascade's `finished` hook
  // (energy_wheel.selector.js:942-951) is what fires the auto-re-fire /
  // findFromStoraga restore.
  //
  // ★ 2026-09-03 — was header-only; the BOARD host ran no cascade at all, so
  // `masterLists` stayed EMPTY and every master-list select on the Heat
  // Exchanger / Configuration / Mounting / Options tabs rendered with no
  // options and no value until the user touched Manufacturer. Legacy's
  // `self.dependenciesHandler.loadOptionsData('mfg_id', 0)` (js:1162) is the
  // last line of the SHARED render path — it runs for a board dialog exactly as
  // it does for the header popup.
  const cascadeFired = useRef(false);
  useEffect(() => {
    if (cascadeFired.current) return;
    if (seeded.current !== modelId) return;
    if (!ewDoc) return;
    // ★ Wait for the tunnel list on the board. `loadParams` narrows the
    // Configuration list by `configDivider`, which on a HYBRID unit is derived
    // from that list — and this cascade runs ONCE, so firing it before the list
    // lands would fetch all 8 configurations and never ask again. The render gate
    // below already waits on the same query; effects do not, so gate here too.
    // (Header mode skips the query, and a FAILED one leaves `isLoading` false
    // rather than stalling the whole dialog.)
    if (tunnelsQ.isLoading) return;
    // legacy js:1027-1031 — `mfg_id: (manufacturer.find(e => e.id === 233) ||
    // manufacturer[0])?.id`, applied in BOTH hosts (the `!self.header` gate on
    // :1035 covers only the catalogue results_filter spread that follows). A
    // board wheel that has never been rated has no `mfg_id` in its
    // `energy_wheel_params` blob, so without this the cascade below could never
    // start and the dialog opened blank.
    if (state.current.mfg_id == null) {
      const mfgRows = ewCmsOptionList(ewDoc as EnergyWheelCmsDoc, 'mfg_id');
      const pick = mfgRows.find((r) => Number(r.id) === 233) ?? mfgRows[0];
      if (pick == null) return;
      state.current = { ...state.current, mfg_id: pick.id };
      init.current = { ...init.current, mfg_id: pick.id };
      ewMfgIdRef.current = pick.id;
      const seededMfg = Number(pick.id);
      if (WBRH_TOGGLE_MFG_IDS.has(seededMfg)) setIsWbTemp(WBRH_TOGGLE_DEFAULTS[seededMfg]);
      bump();
    }
    // ★ the per-manufacturer pins (updateDefaultsDependsOnMFG, js:2247-2290),
    // seeded for the manufacturer the dialog OPENS on — measured against dev,
    // whose Heat Wheel dialog fills Rotor Hub `Ball bearing with shaft`,
    // Speed Control `Constant drive` and Speed `25.00` for Heatex on a wheel
    // whose `energy_wheel_params` is `{"series_id":828}` (verified on dev's own
    // DB for the reported unit) — i.e. with nothing saved to read them from.
    // The graph's leaf `default_values` cannot produce those: they are AI-Rotor
    // shaped (hub [1], speed control [2], speed [12,20]) and Heatex offers
    // hub [2,3] / speed control [4,5] / speed {12,25}, so all three fall through
    // to `null` (or 12) — exactly what dev3 showed.
    //
    // ONLY nulls are filled, so a wheel that carries its own saved value keeps
    // it; and this runs BEFORE the cascade below, so any pin the manufacturer
    // does not actually offer is still dropped/re-picked by applyNewValues the
    // way legacy's `state.set` then `loadOptionsData` ordering does (Heatex's
    // `drive_belt_id: 2` is not in its [1,3] list → re-picked to 3, Power Belt,
    // which is what both apps render).
    const openMfgDefaults = EW_MFG_DEFAULTS[Number(state.current.mfg_id)];
    if (openMfgDefaults) {
      const fill: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(openMfgDefaults)) {
        if (v != null && state.current[k] == null) fill[k] = v;
      }
      if (Object.keys(fill).length) {
        state.current = { ...state.current, ...fill };
        // seeded defaults, not user edits — rebased into the pristine snapshot
        // the same way the `mfg_id` seed above is.
        init.current = { ...init.current, ...fill };
        bump();
      }
    }
    cascadeFired.current = true;
    graph.onParentChange('mfg_id');
    // Legacy fires ONE call — `loadOptionsData('mfg_id', 0)`; `series_id` is a
    // child of mfg_id, so the layered cascade above already reloads it AND then
    // its own children against the NEW series. This second kick is header-only
    // and a no-op there (the header seed has no `series_id`). It must NOT run on
    // the board: a board wheel is seeded with the series_id off its saved
    // `energy_wheel_params` — e.g. `{"series_id": 828}` — and kicking a cascade
    // off a series the new manufacturer does not offer races the mfg cascade
    // with `?series_id=828` child requests that answer `[]`.
    if (headerMode && state.current.series_id != null) graph.onParentChange('series_id');
    // `seedTick` — NOT `detail`: the freshness gate above moves the seed off the
    // commit `detail` first arrives, and a deep-equal refetch is handed back as
    // the SAME reference by structuralSharing, so a `[detail]` dep would never
    // re-run this and the whole option cascade would be skipped on a reopen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerMode, modelId, ewDoc, seedTick, tunnelsQ.isLoading]);

  // ---- field change handlers ----
  const markChanged = useCallback(() => {
    bus.emit('rating_mode:mounting:changed');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bus]);

  const onSearchField = useCallback(
    (name: string, raw: string) => {
      const value = name === 'speed' || name === 'eatr_oacf' ? raw : num(raw);
      const next: Record<string, unknown> = { ...state.current, [name]: value };
      // Selector parity — per-manufacturer pins (updateDefaultsDependsOnMFG,
      // js:2247-2290, called from changeSearchField's mfg branch at js:2442).
      // Applied BEFORE the cascade below, exactly as legacy orders it: the pins
      // are members of the new manufacturer's narrowed lists, so
      // applyParameterNewValues keeps them instead of clearing the field. Without
      // this, Heatex (52) blanked Rotor Hub and Speed Control — the graph's leaf
      // `default_values` are AI-Rotor shaped (hub [1], speed control [2]) and
      // neither survives Heatex's [2,3] / [4,5].
      if (name === 'mfg_id') {
        Object.assign(next, EW_MFG_DEFAULTS[Number(value)] ?? {});
        // Selector parity — the WB/Humidity toggle default follows the NEW
        // manufacturer (showHideInputsDependsOnMfg, js:1495-1509, called from
        // changeSearchField's mfg branch at js:2447):
        //   `if (has_toggle) self.isWbTemp = WBRH_TOGGLE_DEFAULTS[mfg_id]`
        // i.e. Innergytech (60) opens on Humidity, AI Rotor (233) and Annexair
        // (237) on WB Temp. React only seeded the flag at dialog-open, so
        // switching the Manufacturer select left whatever was already there —
        // picking Innergytech on a dialog that opened on another mfg showed
        // WB Temp where dev shows Humidity. Non-toggle mfgs leave it alone,
        // exactly like legacy (the row is hidden for them anyway).
        const nextMfg = Number(value);
        if (WBRH_TOGGLE_MFG_IDS.has(nextMfg)) setIsWbTemp(WBRH_TOGGLE_DEFAULTS[nextMfg]);
      }
      // Selector parity — purge_sector_location interlock
      // (energy_wheel.selector.js:2456-2460): setting it to 1 forces
      // purge_angle_id=1 (and the render disables the Purge Angle control while
      // purge_sector_location_id == 1).
      if (name === 'purge_sector_location_id' && Number(value) === 1) {
        next.purge_angle_id = 1;
      }
      // Selector parity — Heatex-E EATR-OACF auto-force (updateOacfControl,
      // js:2418-2432, invoked from changeSearchField:2481-2483 on mfg/series
      // change): mfg 52 / series 274 forces eatr_oacf true.
      if (name === 'mfg_id' || name === 'series_id') {
        if (Number(next.mfg_id) === 52 && Number(next.series_id) === 274) {
          next.eatr_oacf = true;
        }
      }
      state.current = next;
      // a search-field (param) change invalidates the prior rated outputs.
      setRatedRows(null);
      // cascade descendants if this is a graph parent (mfg/series).
      if (EW_DEPENDENCY_GRAPH[name]) graph.onParentChange(name);
      // ★ HEADER MODE (changeSearchField js:2293-2343):
      //   dataForSave.searchField[name] = value  (js:2329)
      //   mfg_id → self.mfg_id / self.mfg_name off the option (js:2304-2305),
      //            then `self.needToSearch = self.mfg_id;
      //                  self.findFromStoraga(self.mfg_id)` (js:2337-2338) —
      //            i.e. RESTORE that manufacturer's entry instead of saving.
      //   anything else → saveInStorage() (js:2339).
      if (headerMode) {
        dataForSaveRef.current.searchField[name] = value;
        if (name === 'mfg_id') {
          ewMfgIdRef.current = value ?? null;
          ewMfgNameRef.current =
            value == null
              ? null
              : ewCmsSelectedLabel(ewDoc as EnergyWheelCmsDoc, 'mfg_id', value as any) || null;
          const entry = findEntryByMfg(storageMapRef.current, value);
          if (entry) pendingRestore.current = { entry, applyState: true };
        } else {
          saveInStorage();
        }
      }
      markChanged();
      bump();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [graph, markChanged, bump, headerMode, saveInStorage, ewDoc]
  );

  // Selector parity — EATR-OACF checkbox (a search_field, tmpl:574-576/830-832).
  // Legacy changeSearchField reads `e.target.checked` for a checkbox
  // (energy_wheel.selector.js:2437-2439) and routes it to dataForRate.eatr_oacf.
  const onCheckboxField = useCallback(
    (name: string, checked: boolean) => {
      state.current = { ...state.current, [name]: checked };
      setRatedRows(null);
      if (headerMode) {
        dataForSaveRef.current.searchField[name] = checked;
        saveInStorage();
      }
      markChanged();
      bump();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [markChanged, bump, headerMode, saveInStorage]
  );

  const onModelField = useCallback(
    (name: string, raw: string | null) => {
      const value = name.endsWith('_id') ? (raw == null ? null : num(raw)) : raw;
      // Dev-sync J4 — disableSyncIfChanged (energy_wheel.selector.js:
      // 2342-2381): a genuine edit of one of the 12 per-season OA/RA
      // air-condition fields while sync is on silently disables
      // synchronize_air_conditions for the whole unit (local set + raw
      // fire-and-forget PATCH). Board mode only — the header popup has no
      // board unit (legacy bails on the missing ahu the same way).
      if (!headerMode) disableSyncIfAirFieldEdited(name, state.current[name], value);
      const next: Record<string, unknown> = { ...state.current, [name]: value };
      // Selector parity — Annexair pressure-difference mirror (dataForRating,
      // energy_wheel.selector.js:1234-1260): for mfg 237 the two
      // difference_in_pressure_{summer,winter} inputs are kept in lockstep.
      if (
        Number(state.current.mfg_id) === 237 &&
        (name === 'difference_in_pressure_summer' || name === 'difference_in_pressure_winter')
      ) {
        const other =
          name === 'difference_in_pressure_winter'
            ? 'difference_in_pressure_summer'
            : 'difference_in_pressure_winter';
        next[other] = value;
      }
      state.current = next;
      // a perf input change invalidates the rated outputs (must re-rate).
      setRatedRows(null);
      // changeModelField: `dataForSave.modelField[name] = value` (js:2258) then
      // `saveInStorage()` (js:2275).
      if (headerMode) {
        dataForSaveRef.current.modelField[name] = value;
        saveInStorage();
      }
      markChanged();
      bump();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [markChanged, bump, headerMode, saveInStorage]
  );

  // ---- WB/RH toggle (legacy onWBTempHumidityClick) ----
  const onWbRhToggle = useCallback(() => {
    const mfg = Number(state.current.mfg_id);
    if (!WBRH_TOGGLE_MFG_IDS.has(mfg)) return;
    setIsWbTemp((v) => !v);
  }, []);

  // ---- #rate required-field gate (legacy checkRequiredFields, e/w sel:2416) ----
  // BUG 4: the gate must iterate ONLY the per-season RATE field lists
  // (SUMMER/WINTER_REQUIRED_FIELDS_FOR_RATE — airflow/temps/sp/air_stream), NOT
  // the EW_REQUIRED_FIELDS superset. For Heatex(52) the superset carries
  // desiccant/certification/rotor_style/speed_control which are null with no
  // options, so the old gate could NEVER unlock #rate. (EW_REQUIRED_FIELDS still
  // drives select-default seeding elsewhere — untouched.)
  const requiredFilled = useMemo(() => {
    const s = state.current;
    const mfg = Number(s.mfg_id);
    const summerReq = SUMMER_REQUIRED_FIELDS_FOR_RATE[mfg];
    const winterReq = WINTER_REQUIRED_FIELDS_FOR_RATE[mfg];
    if (!summerReq || !winterReq) return false;
    const hasToggle = WBRH_TOGGLE_MFG_IDS.has(mfg);
    const filled = (name: string) => {
      const v = s[name];
      return v != null && v !== '' && v !== '--';
    };
    // for toggle mfgs the list references one of WB/RH; the user may supply the
    // other, so skip those here and validate the WB-or-RH pair separately.
    const toggleFieldNames = new Set([
      'oa_rh_summer', 'ra_rh_summer', 'oa_rh_winter', 'ra_rh_winter',
      'oa_wb_temp_summer', 'ra_wb_temp_summer', 'oa_wb_temp_winter', 'ra_wb_temp_winter',
    ]);
    const seasonValid = (req: string[]) =>
      req.every((name) => (hasToggle && toggleFieldNames.has(name)) || filled(name));
    let validSummer = seasonValid(summerReq);
    let validWinter = seasonValid(winterReq);
    const winterAirflowFilled = filled('oa_airflow_actual_winter') || filled('ra_airflow_actual_winter');
    const summerAirflowFilled = filled('oa_airflow_actual_summer') || filled('ra_airflow_actual_summer');
    if (hasToggle) {
      const summerWBorRH =
        (filled('oa_wb_temp_summer') && filled('ra_wb_temp_summer')) ||
        (filled('oa_rh_summer') && filled('ra_rh_summer'));
      const winterWBorRH =
        (filled('oa_wb_temp_winter') && filled('ra_wb_temp_winter')) ||
        (filled('oa_rh_winter') && filled('ra_rh_winter'));
      validSummer = validSummer && summerWBorRH;
      validWinter = validWinter && winterWBorRH;
    }
    if (validSummer && validWinter) return true;
    if (validSummer && !winterAirflowFilled) return true;
    if (validWinter && !summerAirflowFilled) return true;
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.current, detail]);

  // a saved (rated) EW starts with Select ENABLED; an unrated one stays disabled
  // until a Rate (legacy checkButtonsAvailability).
  useEffect(() => {
    const m = (detail ?? {}) as EWModel;
    setSelectEnabled(Boolean(m.master_energy_wheel_id) || Boolean(ratedRows?.length));
  }, [detail, ratedRows, setSelectEnabled]);

  // #rate visual gate — SCOPED to this selector's own chassis. An un-scoped
  // document.getElementById('rate') would hit the BOARD dialog's #rate first
  // (duplicate ids) whenever the header popup and a board dialog coexist;
  // legacy's own gate is accidentally class-scoped. Discovery §7.3 trap 1.
  useEffect(() => {
    const btn = chassisEl()?.querySelector<HTMLElement>('#rate');
    if (!btn) return;
    // ★ Phase 6.5 — OWN-TAB PREDICATE. The header popup never unmounts a visited
    // tab (component_selector.js:126-186 is show/hide), so all four selectors
    // stay mounted and would fight over the ONE shared `#rate`: whichever
    // rendered last won, and returning to Fan after visiting HEAT WHEEL / PLATE
    // HEAT EXCHANGER left RATE greyed out with valid inputs. Legacy is immune
    // because every gate is scoped to its own tab —
    // `$('#rate.all-rate[data-type="<own>"]')` (fan :1998/:6657, coil
    // :1656/:2677, ewheel :2469, phx :2427) — which matches NOTHING while
    // another tab is active. The chassis stamps `data-type` from the active tab
    // (component_selector.js:173/:181; IndependentRatingPopup `data-type={tab}`)
    // and each selector's `activate.name` IS that tab key, so comparing the two
    // reproduces legacy's scoping exactly.
    // HEADER-ONLY: the board hosts one dialog / one selector / one `#rate`, so
    // its behaviour stays byte-identical (and a board `data-type` that ever
    // disagreed with `eventName` could otherwise freeze the button).
    if (headerMode && btn.dataset.type !== eventName) return;
    btn.classList.toggle('non-clickable', !requiredFilled);
  });

  // ---- section-resize listeners (per BOTH sections — dual tunnel) ----
  useEffect(() => {
    const sec = activate.section as { id?: number; tunnel_id?: number } | undefined;
    const sec2 = activate.section2 as { id?: number; tunnel_id?: number } | undefined;
    const offs: Array<() => void> = [];
    const onResize = () => {
      // legacy sectionSizesChanged repaints the available-sizes readonly inputs;
      // read-only we just bump to re-read the (RTKQ-fresh) section dims.
      bump();
    };
    for (const s of [sec, sec2]) {
      if (!s) continue;
      if (s.tunnel_id != null)
        offs.push(bus.on(`changed:tunnel_${s.tunnel_id}:sizes` as any, onResize));
      if (s.id != null)
        // NB the legacy event key is misspelled 'secrion' — match it verbatim.
        offs.push(bus.on(`changed:secrion_${s.id}:casing_insulation_id` as any, onResize));
    }
    return () => offs.forEach((off) => off());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activate, bump, bus]);

  // ---- build the erwheel RATE input (legacy dataForRate + season stripping) ----
  const buildRateInput = useCallback((): Record<string, unknown> => {
    const s = state.current;
    const mfg = Number(s.mfg_id);
    // dataForRate = the search/model field values (the legacy dataForRating
    // resolves *_id → the CMS .value, but the backend accepts the ids + names;
    // for a faithful port we send the raw collected fields + the resolved names
    // for the doc-resolved selects).
    const input: Record<string, unknown> = {
      // perf fields per season (oa/ra; sa/ea are server-computed but echoed).
      oa_sp: s.oa_sp,
      ra_sp: s.ra_sp,
      min_face_velocity: s.min_face_velocity,
      max_face_velocity: s.max_face_velocity,
      // ★ RATE CONTRACT (pre-existing): innergy_tech validates flow_type ∈
      // {counter,parallel} (data_transformer.py:139-141) — send the STRING value,
      // not the raw flow_type_id. flowTypesQ is a reliably-cached RTK query, with
      // the id→enum map as the belt-and-braces fallback.
      flow_type: ewRateEnumValue(
        flowTypesQ.data as Array<Record<string, unknown>> | undefined,
        s.flow_type_id,
        EW_FLOW_TYPE_ENUM
      ),
      fan_locations: s.fan_locations_id,
      difference_in_pressure_summer: s.difference_in_pressure_summer,
      difference_in_pressure_winter: s.difference_in_pressure_winter,
      // Selector parity — EATR-OACF must reach the vendor rate. Legacy sends
      // `dataForRate.eatr_oacf` (set in dataForRating and force-set to true for
      // Heatex-E in updateOacfControl, energy_wheel.selector.js:2431); React had
      // it ONLY in the Select-save payload. Re-add here with the Heatex-E force.
      eatr_oacf:
        mfg === 52 && Number(s.series_id) === 274 ? true : Boolean(s.eatr_oacf),
      rotor_depth_id: s.rotor_depth_id ?? null,
      height_from_floor: 0,
      // ★ HEADER MODE (REQUIRED — energy_wheel.selector.js:2547):
      //   `tunnel_configuration: !self.header ? self.getDividerFromTunnel() : 'vertical'`
      // — the ONLY header-mode difference in the whole erwheel rate envelope.
      // There is no AHU to derive the divider from, so legacy hardcodes
      // 'vertical'. (`activate.header` is kept as the pre-6.5 fallback path.)
      tunnel_configuration:
        headerMode || (activate as any).header ? 'vertical' : rateDivider,
    };
    // season perf blocks.
    for (const sea of ['summer', 'winter'] as const) {
      for (const pre of ['oa', 'ra'] as const) {
        input[`${pre}_air_stream_${sea}_id`] = s[`${pre}_air_stream_${sea}_id`];
        input[`${pre}_airflow_actual_${sea}`] = s[`${pre}_airflow_actual_${sea}`];
        input[`${pre}_db_temp_${sea}`] = s[`${pre}_db_temp_${sea}`];
        input[`${pre}_wb_temp_${sea}`] = s[`${pre}_wb_temp_${sea}`];
        input[`${pre}_rh_${sea}`] = s[`${pre}_rh_${sea}`];
      }
    }
    // ★ RATE CONTRACT: numbers, not strings — see EW_RATE_NUMERIC_FIELDS.
    for (const k of EW_RATE_NUMERIC_FIELDS) input[k] = rateNum(input[k]);
    // resolve the doc-driven names (mfg→name, series→{id,series}, mounting/voltage).
    // ★ RATE CONTRACT (pre-existing): the backend erwheel handlers read
    // `input["mfg"]` (the NAME) and `input["series"]["id"]` (an OBJECT) —
    // heatex business_processor.py:187-188 KeyError'd on React's `manufacturer`
    // key + bare-string `series` ("Invalid route is selected"). Mirror legacy
    // dataForRating (energy_wheel.selector.js:1286-1289): mfg → the CMS row name,
    // series → {id, series}.
    input.mfg = ewCmsSelectedLabel(ewDoc as EnergyWheelCmsDoc, 'mfg_id', s.mfg_id as any);
    if (s.series_id != null && s.series_id !== '') {
      input.series = {
        id: Number(s.series_id),
        series: ewCmsSelectedLabel(ewDoc as EnergyWheelCmsDoc, 'series_id', s.series_id as any),
      };
    }
    input.mounting_arr = ewCmsSelectedLabel(ewDoc as EnergyWheelCmsDoc, 'mounting_arr_id', s.mounting_arr_id as any);
    input.mtr_voltage = ewCmsSelectedLabel(ewDoc as EnergyWheelCmsDoc, 'mtr_voltage_id', s.mtr_voltage_id as any);
    input.mtr_voltage_id = s.mtr_voltage_id;
    // master-list selects: send the resolved .value where available (the rate
    // backend keys on the value strings for these), falling back to the id.
    for (const field of Object.keys(PARAMETER_NAMES)) {
      const key = field.replace(/_id$/, '');
      const rows = masterListsRef.current[field];
      const row = rows?.find((r: any) => String(r.id ?? r.value) == String(s[field]));
      input[key] = (row as any)?.value ?? s[field];
      if (field === 'configuration_id') input.configuration_id = s.configuration_id;
    }

    // ★ RATE CONTRACT (pre-existing): force the enum-validated master fields to
    // their STRING value. The loop above resolves from `masterListsRef`, but the
    // header-mode auto-fire rates before the cascade caches those lists, so
    // type/fin_pattern otherwise ship as the raw id (type:1 → heatex 400 "Input
    // should be 'sensible' or 'total'"; innergy is_valid() → the backend
    // set-crash). ewRateEnumValue prefers the cached `.value`, else the id→enum
    // map — mirrors legacy `model.value` (energy_wheel.selector.js:1271).
    input.type = ewRateEnumValue(masterListsRef.current['type_id'], s.type_id, EW_TYPE_ENUM);
    input.fin_pattern = ewRateEnumValue(
      masterListsRef.current['fin_pattern_id'],
      s.fin_pattern_id,
      EW_FIN_PATTERN_ENUM
    );
    // ★ RATE CONTRACT: innergy_tech requires `speed` (numeric) present
    // (data_transformer.py required_common_fields); the React port omitted it.
    // Legacy sends it from the Speed select (default 20 on dev3).
    input.speed = s.speed != null && s.speed !== '' ? Number(s.speed) : 20;

    // WB/RH stripping (legacy 2552-2562).
    if (mfg == 52) {
      delete input.oa_rh_winter; delete input.ra_rh_winter;
      delete input.oa_rh_summer; delete input.ra_rh_summer;
    } else if (!WBRH_TOGGLE_MFG_IDS.has(mfg)) {
      delete input.oa_wb_temp_winter; delete input.ra_wb_temp_winter;
      delete input.oa_wb_temp_summer; delete input.ra_wb_temp_summer;
    }
    // blank-season deletion (legacy 2566-2580): no oa_airflow ⇒ delete the
    // season's required fields EXCEPT oa_sp/ra_sp.
    if (input.oa_airflow_actual_winter == null || input.oa_airflow_actual_winter === '--') {
      (WINTER_REQUIRED_FIELDS_FOR_RATE[mfg] ?? []).forEach((k) => {
        if (k !== 'oa_sp' && k !== 'ra_sp') delete input[k];
      });
    }
    if (input.oa_airflow_actual_summer == null || input.oa_airflow_actual_summer === '--') {
      (SUMMER_REQUIRED_FIELDS_FOR_RATE[mfg] ?? []).forEach((k) => {
        if (k !== 'oa_sp' && k !== 'ra_sp') delete input[k];
      });
    }
    if (input.altitude == null) input.altitude = 0;
    return input;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activate, configDivider, ewDoc, headerMode, flowTypesQ.data]);

  // ---- RATE (CODE-ONLY, userArmed) ----
  // `index` mirrors legacy `rate(e, index)` → `self.rated_erwheel_index = index ||
  // self.saved_rated_erwheel_index || 0` (energy_wheel.selector.js:2595-2596),
  // i.e. the restored row index the header-mode auto-re-fire passes in
  // (`self.rate(null, model.rated_erwheel_index)`, js:1383).
  // Set just before a re-rate the header popup runs by itself (restoring a
  // saved entry, or a manufacturer switch): that is not a user's rating, so
  // it is kept out of component_rated.
  const autoRateRef = useRef(false);
  const doRate = useCallback(async (index = 0) => {
    const trackRating = !autoRateRef.current;
    autoRateRef.current = false;
    if (!requiredFilled) return;
    userArmed.current = true;
    setRateErrors([]);
    setRateWarnings([]);
    const input = buildRateInput();
    // ★ HEADER MODE: legacy's `dataForRate` IS the rate input, and it is what the
    // saved entry carries (js:1394). React builds the whole input in one pass
    // instead of accumulating it field-by-field through `dataForRating`, so the
    // ref is refreshed here — a timing-only deviation: the persisted value is the
    // same map the legacy popup would restore into `self.dataForRate`.
    if (headerMode) dataForRateRef.current = input;
    const envelope = buildRatingEnvelope('erwheel', state.current.mfg_id as any, input);
    const body = await dispatchRating(envelope, { track: trackRating });
    if (body.cancelled) return;
    // legacy `self.rated_datetime = self.generetionTimeStamp()`
    // (ewheel.selector.js:2742/:2752/:2760) — `YYYY.MM.DD_HH:mm:ss`, not a
    // locale string; see ratingTimestamp.ts.
    ratedDatetime.current = generateRatingTimestamp();
    if (body.ok && body.models && body.models.length) {
      // sortRotaryHtexTable: numeric sort by wheel_size when >1 model.
      const rows = [...body.models];
      if (rows.length > 1) rows.sort((a, b) => Number(a.wheel_size) - Number(b.wheel_size));
      setRatedRows(rows);
      const idx = rows[index] ? index : 0;
      setRatedIndex(idx);
      savedRatedIndex.current = null; // js:2596 clears saved_rated_erwheel_index
      setRateWarnings((body.warnings ?? []).filter(Boolean));
      setSelectEnabled(true);
      // ★ HEADER MODE: the rate tail saves (js:2864, at the end of the
      // rated-output render) — this is what stamps `rate:true` so the next popup
      // construction AUTO-RE-FIRES the rate.
      if (headerMode) saveInStorage({ rate: true, ratedIndex: idx });
    } else {
      setRatedRows([]);
      setRateErrors((body.errors ?? []).filter(Boolean));
      setRateWarnings((body.warnings ?? []).filter(Boolean));
      // legacy `rate: !!self.rated_erwheels?.[…]` ⇒ false with no rows.
      if (headerMode) saveInStorage({ rate: false, ratedIndex: 0 });
    }
    bump();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requiredFilled, buildRateInput, dispatchRating, setSelectEnabled, bump, headerMode, saveInStorage]);

  // ---- ★ Phase 6.5 — HEADER-MODE pending restore + AUTO-RE-FIRE -------------
  // Legacy hangs both off the DependenciesHandler `finished` callback
  // (energy_wheel.selector.js:942-951): once the cascade settles it either
  // re-fires the rate (`needToRate` → `self.rate()`) or re-applies the
  // manufacturer's saved state and rates (`findFromStoraga(needToSearch, true)`
  // → js:1383 `if (model.rate) self.rate(null, model.rated_erwheel_index)`).
  // React has no `finished` hook, so the cascade is settle-detected (isCascading
  // false through one 250 ms tick) — the pattern FanSelector established. NO dep
  // array on purpose: the effect re-arms every render, which is how a
  // still-settling cascade postpones itself.
  useEffect(() => {
    if (!headerMode) return undefined;
    const pending = pendingRestore.current;
    if (!pending) return undefined;
    if (!ewDoc) return undefined;
    if (!cascadeFired.current) return undefined;
    if (graph.isCascading) return undefined;
    const t = window.setTimeout(() => {
      if (graph.isCascading) return; // still settling — a later render re-arms
      if (pending.applyState) {
        // findFromStoraga js:1334-1344 — apply the entry's current_model + state
        // (+ er_wheel_mfg / generic_mfg / saved index). Legacy spreads
        // `{...default_values, ...model.current_model, ...model.state}`.
        state.current = {
          ...state.current,
          ...((pending.entry.current_model as Record<string, unknown>) ?? {}),
          ...((pending.entry.state as Record<string, unknown>) ?? {}),
        };
        ewMfgNameRef.current = (pending.entry.er_wheel_mfg as string | null) ?? null;
        genericMfgRef.current = pending.entry.generic_mfg ?? false;
        savedRatedIndex.current = (pending.entry.rated_erwheel_index as number | null) ?? null;
        dataForRateRef.current = (pending.entry.dataForRate as Record<string, unknown>) ?? {};
        dataForSaveRef.current =
          (pending.entry.dataForSave as {
            modelField: Record<string, unknown>;
            searchField: Record<string, unknown>;
          }) ?? { modelField: {}, searchField: {} };
        // phase 2 (the rate) runs on the next pass so requiredFilled has
        // re-derived from the freshly applied state.
        pendingRestore.current = { entry: pending.entry, applyState: false };
        bump();
        return;
      }
      pendingRestore.current = null;
      // js:1383 / js:945 — gated on `rate` (UNLIKE coil, whose 1 s click is not).
      if (pending.entry.rate) {
        autoRateRef.current = true;
        void doRate(savedRatedIndex.current ?? 0);
      }
    }, 250);
    return () => window.clearTimeout(t);
  });

  // ---- SELECT (selectErWheel — CODE-ONLY, userArmed) ----
  // ★ UNREACHABLE IN HEADER MODE: `#select` is never rendered
  // (component_selector.tmpl:131-136) and the `:selector:select` subscription is
  // omitted below, so neither the __FORM_SHIM__ model.set nor
  // `rating_mode:hide:<id>` can ever run there.
  const getFormShim = (): ComponentFormShim | undefined =>
    window.__FORM_SHIM__?.[modelId];

  // generic_mfg from the mfg select option's data-generic (legacy 1792-1794).
  // SCOPED to this selector's own chassis (see chassisEl) — an un-scoped
  // `#er-wheel …` read would find the board dialog's select when both hosts are
  // mounted. Discovery §7.3 trap 1.
  const readGenericMfg = (): boolean => {
    const el =
      chassisEl()?.querySelector<HTMLSelectElement>('#er-wheel select[name="mfg_id"]') ?? null;
    const opt = el?.selectedOptions?.[0];
    const generic = opt?.dataset?.generic;
    return generic != null && generic !== 'none';
  };

  const doSelect = useCallback(() => {
    userArmed.current = true;
    const s = state.current;
    const shim = getFormShim();
    // no-rate fallback (legacy 1795-1807): never rated → set the changed state
    // (+ airflow companions when air_stream toggled) + hide.
    if (!ratedRows || !ratedRows.length) {
      const changed: Record<string, unknown> = {};
      for (const k of Object.keys(s)) {
        if (s[k] !== init.current[k]) changed[k] = s[k];
      }
      if (changed.oa_air_stream_summer_id) changed.oa_airflow_actual_summer = s.oa_airflow_actual_summer;
      if (changed.ra_air_stream_summer_id) changed.ra_airflow_actual_summer = s.ra_airflow_actual_summer;
      if (changed.ra_air_stream_winter_id) changed.ra_airflow_actual_winter = s.ra_airflow_actual_winter;
      if (changed.oa_air_stream_winter_id) changed.oa_airflow_actual_winter = s.oa_airflow_actual_winter;
      shim?.model.set(changed);
      shim?.render();
      finishSelect();
      return;
    }
    // rated path: build models_field + params_field (the §33.3 write).
    const r = ratedRows[ratedIndex] as Record<string, any>;
    const generic = readGenericMfg();
    const { modelsField } = buildSelectPayload(
      { ...s, __generic_mfg: generic, __rated_datetime: ratedDatetime.current },
      r,
      masterListsRef.current,
      // legacy's `.model_field` sweep over the live dialog DOM
      // (ewheel.selector.js:2054-2066) — this is what supplies oa_sp / ra_sp /
      // oa_db_temp_winter / the four air-stream ids. Read here, while the
      // dialog is still mounted: finishSelect() unmounts it below.
      sweepModelFieldInputs(
        rootRef.current,
        // legacy `!i.includes('wb_temp')`; `elevation` is diverted into
        // params_field.altitude, which buildSelectPayload already sets from
        // `s.elevation` (legacy renames the input itself at :1564, so both
        // spellings are skipped).
        (n) => n.includes('wb_temp') || n === 'elevation' || n === 'altitude'
      )
    );
    // Dev-sync J4 — the Checkpoint-4 switch (energy_wheel.selector.js:
    // 2087-2094): POST energy_wheels/<id>/select with models_field; onCommit
    // = commitErWheelChain (:2083-2085 — current_model.set(models_field)
    // only). Board mode only (the header popup rates detached models with no
    // /select route); header keeps the plain set. No autoRefresh flag —
    // a non-empty cascade re-reads the rows it changed (guardSelect default).
    if (!headerMode) {
      const selectChain = guardSelect({
        url: `/api/v1.0/ahu/energy_wheels/${modelId}/select`,
        analyticsComponent: 'energy_wheel',
        payload: modelsField,
        onCommit: () => {
          shim?.model.set(modelsField);
          shim?.render();
        },
      });
      // finishSelect() below closes the dialog on the CLICK (parity — legacy
      // fires `rating_mode:hide` before the save chain), so the POST above runs
      // on detached. A dialog reopened before it settles would GET the
      // pre-select row — legitimately fresh, and still without a
      // `master_energy_wheel_id`. Make that reopen wait for the write instead
      // (selectWriteGate.ts).
      trackSelectWrites(selectWriteKey('energy_wheels', modelId), selectChain);
      void selectChain;
    } else {
      shim?.model.set(modelsField);
      shim?.render();
    }
    finishSelect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ratedRows, ratedIndex, ewDoc, modelId, headerMode]);

  const finishSelect = useCallback(() => {
    window.setTimeout(() => bus.emit('minRequiredLengthChange'), 1000);
    bus.emit('rating_mode:hide');
    bus.emit(`rating_mode:hide:${modelId}`, { selected: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId, bus]);

  // ---- RESET (energy_wheel_<id>:selector:reset) --------------------------
  // Port of energy_wheel.selector.js:1821-1854, dispatched from the chassis
  // `#reset` icon through the fan-out subscribed at js:1067-1071.
  //
  // Legacy, in order:
  //   :1823  remember `mfg_id` (+ the selected option's `data-generic`)
  //   :1825  `self.rate_request?.abort()`
  //   :1826-1834  rebuild `model_data.params_energy_wheel` as `null` for every
  //          `<select class="search_field">` in the dialog
  //   :1835  `self.state.set(...)` that map
  //   :1836-1845  re-select the mfg `<option>` that was selected before
  //   :1847  `$select.trigger('change')`  ← the workhorse
  //
  // That last line is a real `change` event on `select.search_field[name=mfg_id]`,
  // so it runs `changeSearchField` (js:2434) down the `name == 'mfg_id'` path:
  //   :2443 updateDefaultsDependsOnMFG · :2450 showHideInputsDependsOnMfg
  //   :2465 `state.set('mfg_id', value)` (writes the preserved id back)
  //   :2471 `checkRequiredFields()`
  //   :2472 `.output-part` innerHTML = ''   (the rated rows are wiped)
  //   :2473 `rate_request?.abort()`
  //   :2474 `#select` → `non-clickable`
  //   :2475 `dependenciesHandler.loadOptionsData('mfg_id', 0)`  ← re-cascade
  //   :2477-2479 mfg branch → `findFromStoraga` (NOT `saveInStorage`)
  //   :2481 `updateOacfControl()`
  const doReset = useCallback(() => {
    // js:1825 / :2473 — the same in-flight abort `:selector:cancel` uses.
    abortRating();
    // js:1823 + :1826-1835 + :1836-1847 — blank every search-field SELECT,
    // keep the manufacturer. Legacy nulls `mfg_id` too and only writes it back
    // one tick later via the restored <option> + `trigger('change')`; React has
    // no DOM round-trip to make that observable, so the preserved value goes
    // back in the same object write. Net state is identical.
    // (The `data-generic` half of legacy's re-selection needs no port: React's
    // `DocSelect` stamps `data-generic` per option and drives `value` from
    // state, so restoring `mfg_id` restores the same option — and `readGenericMfg`
    // reads it back off the DOM at #select time.)
    const mfg = state.current.mfg_id;
    const next: Record<string, unknown> = { ...state.current };
    for (const f of EW_RESET_SEARCH_SELECTS) next[f] = null;
    next.mfg_id = mfg ?? null;
    // js:2442 — the `trigger('change')` at :1847 runs the mfg branch, whose first
    // statement is `updateDefaultsDependsOnMFG`. Its pins land ON TOP of the
    // blanking, before the cascade at the end of this callback.
    Object.assign(next, EW_MFG_DEFAULTS[Number(mfg)] ?? {});
    // …and its `showHideInputsDependsOnMfg(mfg)` (js:2447), which re-seeds the
    // WB/Humidity toggle from WBRH_TOGGLE_DEFAULTS. Reset keeps the
    // manufacturer, so this restores that manufacturer's default view.
    if (WBRH_TOGGLE_MFG_IDS.has(Number(mfg))) setIsWbTemp(WBRH_TOGGLE_DEFAULTS[Number(mfg)]);
    state.current = next;
    // `init.current` is deliberately NOT re-based. Legacy's "what changed"
    // baseline is Backbone's `state.changed`, and the `state.set` at :1835 ADDS
    // the blanked fields to it — so a subsequent no-rate #select pushes those
    // nulls (js:1795-1807, the React branch at doSelect above). Re-basing the
    // React snapshot would swallow them. (FanSelector.doReset does re-base
    // because fan.selector.js's reset rebuilds `model_data` wholesale; the heat
    // wheel's does not.)
    //
    // js:2472 — `.output-part` innerHTML = '' wipes the whole rated pane, which
    // is the rows AND any rate errors/warnings rendered beside them.
    setRatedRows(null);
    setRateErrors([]);
    setRateWarnings([]);
    // js:2474 — `#select` back to non-clickable. NOTE the pre-existing effect
    // that owns this flag re-enables it for a wheel that already has a saved
    // `master_energy_wheel_id`; left alone rather than reworked (additive-only).
    setSelectEnabled(false);
    markChanged();
    bump();
    // ★ THE WORKHORSE. `$select.trigger('change')` bottoms out in
    // `dependenciesHandler.loadOptionsData('mfg_id', 0)` (js:2475), which
    // re-walks the dependency graph from the manufacturer and reloads +
    // re-picks every descendant option list. `useDependencyGraph` IS
    // DependenciesHandler, and `graph.onParentChange('mfg_id')` IS that call —
    // it is exactly what the mfg `onSearchField` path already runs (see the
    // `if (EW_DEPENDENCY_GRAPH[name]) graph.onParentChange(name)` above).
    // Nulled children are then re-picked by applyNewValues.
    //
    // NOT ported from the mfg branch:
    //  - `findFromStoraga` (js:2478): legacy's header popup would restore that
    //    manufacturer's localStorage entry here, i.e. immediately UNDO the
    //    blank. Unreachable in practice (the header chassis renders with
    //    `show_tab:'all'` and never draws `#reset`), and resurrecting stale
    //    saved state out of a defensive path is worse than doing nothing, so
    //    header mode neither restores nor persists on reset. Legacy's mfg
    //    branch does not `saveInStorage` either.
    //  - `updateOacfControl` (js:2481): it only fires for mfg 52 + series 274,
    //    and `series_id` is null at this point, so it is a no-op by
    //    construction. The cascade may re-pick series 274, but legacy's own
    //    re-pick goes through `model.set` and does not re-run it either.
    if (mfg != null) {
      // ★ arm the legacy leaf defaults for THIS cascade only — see
      // `resetCascade` / `pickFallback` above. Without it a board reset
      // would blank ~24 selects and never refill them.
      resetCascade.current = true;
      graph.onParentChange('mfg_id');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abortRating, graph, markChanged, bump, setSelectEnabled]);

  // Disarm on the falling edge of the cascade. `isCascading` is derived from
  // useDependencyGraph's in-flight counter and every load start/finish forces a
  // render, so a dep-less effect sees both edges.
  const wasCascading = useRef(false);
  useEffect(() => {
    if (graph.isCascading) wasCascading.current = true;
    else if (wasCascading.current) {
      wasCascading.current = false;
      resetCascade.current = false;
    }
  });

  // ---- chassis fan-out subscriptions (§30.4) ----
  useEffect(() => {
    const offs = [
      bus.on(`${eventName}_${scope}:selector:rate` as any, () => void doRate()),
      // ★ HEADER MODE omits `:selector:select` entirely. `.control-buttons`
      // (#select + #cancel) is wrapped in `{{#ifCompare show_tab '!=' 'all'}}`
      // (component_selector.tmpl:131-136), so the header chassis can never
      // dispatch it — legacy subscribes anyway (energy_wheel.selector.js:998-1002)
      // but the handler is unreachable. Not subscribing makes header mode's
      // ZERO-WRITES property structural rather than incidental (discovery §3.1).
      ...(headerMode
        ? []
        : [bus.on(`${eventName}_${scope}:selector:select` as any, () => doSelect())]),
      bus.on(`${eventName}_${scope}:selector:cancel` as any, () => abortRating()),
      // energy_wheel.selector.js:1067-1071 — the chassis `#reset` icon.
      bus.on(`${eventName}_${scope}:selector:reset` as any, () => doReset()),
      bus.on('cancel:dialog:changes', () => abortRating()),
      // NOTE no `:selector:print` subscription in EITHER host: print is
      // SUPPRESSED for energy_wheel in header mode — the chassis stamps
      // `hide-print` on `.ahu-rate-content` (component_selector.js:175/:183) and
      // `.ahu-rate-content.hide-print #print{opacity:0;pointer-events:none}`
      // (common.css:12593-12596) makes the icon unclickable (observed live).
    ];
    return () => offs.forEach((off) => off());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventName, scope, bus, headerMode, doRate, doSelect, abortRating]);

  // abort any in-flight rate on unmount (data-safety).
  useEffect(() => () => abortRating(), [abortRating]);

  // ---- render ----
  if (
    detailLoading ||
    ewDocLoading ||
    airStreamsQ.isLoading ||
    flowTypesQ.isLoading ||
    fanLocationsQ.isLoading ||
    pricingMethodsQ.isLoading ||
    // (skipped in header mode, where isLoading stays false)
    tunnelsQ.isLoading ||
    seeded.current !== modelId
  ) {
    return <div className="preloader dotted-preloader active" />;
  }

  const s = state.current;
  const mfg = Number(s.mfg_id);
  // Selector parity — the mfg-gating flags legacy applies in
  // showHideInputsDependsOnMfg (energy_wheel.selector.js:1495-1520):
  //   hide-for-annexair  → hidden when mfg 237
  //   show-for-annexair  → shown ONLY when mfg 237
  //   show-for-heatex    → shown ONLY when mfg 52 (WB Temperature rows)
  //   show-for-wbrh-toggle → shown ONLY for the toggle mfgs (60/233/237)
  const isAnnexair = mfg === 237;
  const isHeatexE = mfg === 52 && Number(s.series_id) === 274;
  const purgeLocked = Number(s.purge_sector_location_id) === 1;
  // Mounting tab is hidden for Annexair (nav li + pane, ewheel.selector.tmpl:15 /
  // :950). If it was the active tab when the mfg flips to Annexair, fall back to
  // Heat Exchanger so the pane never renders blank.
  const effTab: InputTab = isAnnexair && activeTab === 'Mounting' ? 'Properties' : activeTab;
  // the 6 input sub-tabs in legacy nav order (Mounting dropped for Annexair).
  const inputTabs: InputTab[] = (
    ['Properties', 'Performance', 'Configuration', 'Mounting', 'Options', 'Pricing'] as InputTab[]
  ).filter((t) => !(t === 'Mounting' && isAnnexair));
  const tabLabel = (t: InputTab): string => (t === 'Properties' ? 'Heat Exchanger' : t);
  // available-airway readonly display sources (dual-tunnel — tmpl:327-345).
  // A rotary wheel spans BOTH tunnels, so legacy renders one row per section it
  // sits in, labelled with that section's tunnel index NAME — "(1:1)", "(2:1)" —
  // not an ordinal (tmpl:327-345 `{{#each tunnels}} … ({{index}})`, fed by
  // js:489-501 `index: s.tunnel.get_index().name`). The React port had neither
  // half: `section2` was never put on the activate payload (so only the primary
  // tunnel's row was ever drawn) and the label was a 1-based counter.
  const ewSections = [activate.section, activate.section2]
    .filter(Boolean)
    .map((sec): Record<string, unknown> & { index: string } => {
      const s2 = sec as Record<string, unknown>;
      const tunnel = (tunnelsQ.data ?? []).find((t) => t.id === s2.tunnel_id);
      // ★ On a HYBRID unit the legacy `index` label is not unique: it comes from
      // the master tunnel-index row (`index_id`), which hybrid addressing no
      // longer maintains — both tunnels of a stacked pair still read "1:1", so
      // the two REQUIRED AIRWAY rows came out under identical labels. Address
      // those from row/level/position instead, via the canonical formatter
      // (`addressing.ts`, which models/ahuBoard.ts points `index` readers at).
      return {
        ...s2,
        index: ahu?.is_hybrid
          ? formatTunnelAddress(tunnel ?? {}, true)
          : tunnel?.index ?? '',
      };
    });
  const oneDec = (v: unknown): string =>
    v == null || v === '' ? '' : String(Number(v).toFixed(1));
  const streams = (airStreamsQ.data as OptionRow[] | undefined) ?? [];
  const rowsOf = (q: { data?: unknown }): OptionRow[] => (q.data as OptionRow[] | undefined) ?? [];

  const tabClass = (t: InputTab) =>
    `tab-pane fade scrollable-content col-xs-12 float-left${effTab === t ? ' in active' : ''}`;

  const rows = ratedRows ?? [];
  // The port's `renderRatedOutputs` moment — the condition the output block below
  // renders on. Legacy's `rating` class goes on at exactly the same point.
  const hasRatedOutput = ratedRows != null || Boolean(s.master_energy_wheel_id);

  // Everything the module-scope sub-views close over (see the EwViewCtx note).
  // Deliberately NOT memoised: `s` is a mutable ref read, so a stale value would
  // be worse than the re-render a new object costs.
  const ewCtx: EwViewCtx = {
    s, mfg, season, isWbTemp, isHeatexE, isAnnexair, streams, masterLists, ewDoc,
    optionIds: graph.optionIds,
    onSearchField, onModelField, onCheckboxField, onWbRhToggle,
  };

  return (
    <EwCtx.Provider value={ewCtx}>
    {/* ★ JOB B — LEGACY DOM RESTORE (supersedes #960). Legacy's view `el` is
        `<div class="col-xs-12 rotary-htex">` (the WRAPPER) holding TWO siblings:
        `#er-wheel` (the input pane, ewheel.selector.tmpl:2) and the rated-output
        block (tmpl:1184). React had put `rotary-htex` ON `#er-wheel` and nested the
        output inside it, so EVERY `.rotary-htex #er-wheel …` rule stopped matching —
        the 360px input-pane cap (common.css:19448, why the rated table drew over
        Properties) and `select{width:calc(100% - 20px)}` (ragged widths). Restoring
        the wrapper > input-pane + sibling-output structure re-arms all of them; the
        `coil_new` class restores the 13px / 29px row sizing.
        The Provider around it carries the render state the module-scope sub-views
        used to close over; it emits no DOM, so every selector above is untouched. */}
    <div ref={rootRef} className="col-xs-12 rotary-htex">
      {/* ===== INPUT PANE (#er-wheel) — a CHILD of .rotary-htex ===== */}
      {/* ★ PARITY — `rating` is NOT a template class. Legacy stamps it on
          `.er-wheel` from renderRatedOutputs (energy_wheel.selector.js:3037),
          i.e. only once a rate has produced the output sheet; the port hard-coded
          it, so it was on from the first paint. It is load-bearing:
          `.coil-rating-mode.rating .scrollable-content:nth-child(3),:nth-child(4)`
          (common.css:19009) pins the Configuration and Mounting panes at 350px,
          and `.images_list{height:100%}` (:19497) only resolves against a pane
          with a RESOLVED height — so on Mounting the fan-location diagram box
          grew from dev's content height (327px) to 350px and its wrapper (379px)
          overhung the 360px input pane. Gated on the same expression that renders
          the output block below, which is this port's `renderRatedOutputs`. */}
      <div
        id="er-wheel"
        className={`coil_new input-part er-wheel coil-rating-mode energy_wheel${
          hasRatedOutput ? ' rating' : ''
        }`}
      >
        {/* available-sizes wrap kept for the section-resize repaint hook */}
        <div className="section-available-sizes-wrap" />

        {/* input tab nav — the SIX legacy sub-tabs (tmpl:3-25) */}
        <div className="col-xs-1 nav nav-tabs input-menu">
          <ul>
            <li className="cursor-default">Input</li>
            {inputTabs.map((t) => (
              <li key={t} className={`input_label ${t}${effTab === t ? ' selected active' : ''}`}>
                <a className={effTab === t ? 'active' : undefined} onClick={() => setActiveTab(t)}>
                  {tabLabel(t)}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* ★ PARITY (ewheel.selector.tmpl:27-28) — legacy's pane column carries an
            inline `padding-top:18px; padding-bottom:15px` and wraps ALL six panes
            in a `<div class="row">`. The port dropped both. Without the `.row`
            its −15px side margins never cancel the panes' own `col-xs-12` 15px
            padding, so every tab rendered 15px right of dev and 30px narrower —
            which is why the Mounting/Options/Pricing labels and selects all sat
            7.5px off. Without the padding every tab started 18px too high. */}
        <div
          className="col-xs-11 tab-content content-data full-height right"
          style={{ paddingTop: 18, paddingBottom: 15 }}
        >
         <div className="row">
          {/* ----- Heat Exchanger [Properties] (tmpl:30-347) -----
              id `{{prefix}}Properties`; `prefix: self.header ? '' : 'ahu-'`, so the
              pane ids drop the prefix in header mode (no board-pane collision). */}
          <div className={tabClass('Properties')} id={`${idPrefix}Properties`}>
            {/* col-lg-5: mfg, series, type, desiccant, Rotor Size, rotor_style,
                rotor_depth, hub, seal, certification */}
            <div className="col-xs-12 col-md-12 col-lg-5 pr-30">
              <DocSelect name="mfg_id" label="Manufacturer" />
              {/* Shown for Annexair too (2026-10-05, asked for in #box-anna).
                  Legacy hides this row for Annexair (`hide-for-annexair`,
                  ewheel.selector.tmpl:50) — but legacy only hides it with a CSS
                  class, leaving the select populated and `series_id` set, where
                  React dropped the field from the DOM entirely and left the user
                  nothing to resolve a null series with. */}
              <DocSelect name="series_id" label="Series" />
              <MasterSelect name="type_id" label="Type" labelKey="name" />
              <MasterSelect name="desiccant_id" label="Desiccant" labelKey="name" />
              <MasterSelect name="wheel_size_id" label="Rotor Size" />
              <MasterSelect name="rotor_style_id" label="Rotor Style" labelKey="name" />
              {!isAnnexair && <MasterSelect name="rotor_depth_id" label="Rotor Depth" unit="in" />}
              {!isAnnexair && <MasterSelect name="hub_id" label="Rotor Hub" labelKey="name" />}
              {!isAnnexair && <MasterSelect name="seal_id" label="Seal" labelKey="name" />}
              {!isAnnexair && <MasterSelect name="certification_id" label="Certification" labelKey="name" />}
            </div>
            {/* col-lg-7: fin_pattern, fin_thickness, well_height, casing,
                casing_type, speed, speed_control, drive_belt, mtr_voltage,
                fan_locations (annexair-only), Available Airway (readonly) */}
            <div className="col-xs-12 col-md-12 col-lg-7 performance-group">
              {!isAnnexair && <MasterSelect name="fin_pattern_id" label="Fin Pattern" labelKey="name" />}
              {!isAnnexair && <MasterSelect name="fin_thickness_id" label="Fin Thickness" unit="in" />}
              <MasterSelect name="well_height_id" label="Well Height" />
              {!isAnnexair && <MasterSelect name="casing_id" label="Casing" labelKey="name" />}
              {!isAnnexair && <MasterSelect name="casing_type_id" label="Casing Type" labelKey="name" />}
              {/* Speed is a SELECT in legacy (tmpl:254-266, `<select name="speed">`
                  off the graph's speed list), not a free-text box. The write
                  path stays `onSearchField` — legacy's `includeAsIs`
                  (energy_wheel.selector.js:1180) keeps speed a raw value. */}
              <MasterSelect name="speed" label="Speed" unit="RPM" />
              {!isAnnexair && <MasterSelect name="speed_control_id" label="Speed Control" labelKey="name" />}
              {!isAnnexair && <MasterSelect name="drive_belt_id" label="Drive Belt" labelKey="name" />}
              {!isAnnexair && <DocSelect name="mtr_voltage_id" label="Motor Voltage" />}
              {/* fan_locations copy — show-for-annexair (tmpl:310-326) */}
              {isAnnexair && (
                <ModelSelect name="fan_locations_id" label="Fan Locations" options={rowsOf(fanLocationsQ)} />
              )}
              {/* Markup is legacy's verbatim (tmpl:327-345) — the row splits
                  6/6 like every other row in this column, and the flex pair
                  sits inside the standard `.row > .col-xs-12` wrapper. React
                  had split it 5/7 and dropped the wrapper, which moved the
                  input pair left of the selects above it and stretched it: the
                  wrapper is what `.coil_new .content-data .input-part > .row
                  div {padding-left:0}` (common.css:16028) acts on, so without
                  it the field is sized off the bare negative row margin. */}
              {ewSections.map((sec, i) => (
                <div className="row" key={i}>
                  <div className="col-xs-6 label-part h-29">
                    Available Airway W x H ({sec.index})
                    <span className="unit right">in</span>
                  </div>
                  <div className="col-xs-6 input-part">
                    <div className="row">
                      <div className="col-xs-12">
                        <div
                          className="row display-flex-row section-available-sizes"
                          data-section={String(sec.id ?? '')}
                        >
                          <input value={oneDec(sec.aw_width)} readOnly disabled tabIndex={-1} />
                          <span className="unit ">X</span>
                          <input value={oneDec(sec.aw_height)} readOnly disabled tabIndex={-1} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ----- Performance (summer/winter tabs, tmpl:348-875) ----- */}
          <div className={tabClass('Performance')} id={`${idPrefix}Performance`}>
            {/* tmpl:350-358 — winter listed FIRST, lower-case labels, and the
                `<ul>` is `col-xs-12 text-right` inside a `col-xs-12 > .row`,
                which is what puts the toggle at the RIGHT of the pane (dev).
                A bare `list-inline` ul floated it left AND out-specified
                `.summer-winter-tabing li {display:inline; padding:10px 0}`
                (common.css:19078) with Bootstrap's own `li` rule, so the two
                labels ran together as "wintersummer". */}
            <div className="col-xs-12">
              <div className="row">
                <ul className="col-xs-12 text-right summer-winter-tabing">
                  <li className={`winter-tab${season === 'winter' ? ' active' : ''}`}>
                    <a tabIndex={-1} data-toggle="tab" data-tab="winter"
                      onClick={() => setSeason('winter')}>winter</a>
                  </li>
                  <li className={`summer-tab${season === 'summer' ? ' active' : ''}`}>
                    <a tabIndex={-1} data-toggle="tab" data-tab="summer"
                      onClick={() => setSeason('summer')}>summer</a>
                  </li>
                </ul>
              </div>
            </div>
            {/* tmpl:359 — both season panes live in ONE `.tab-content` column
                that is width-capped at large/xl (col-lg-10 / col-xl-7); the port
                let them run the full pane width. */}
            <div className="col-xs-12 col-lg-10 col-xl-7 tab-content">
              <SeasonBlock sea="summer" />
              <SeasonBlock sea="winter" />
            </div>
          </div>

          {/* ----- Configuration (tmpl:876-949) — 5/7 split, unlike the other
              panes, and a SECOND column holding the configuration diagrams. */}
          <div className={tabClass('Configuration')} id={`${idPrefix}Configuration`}>
            <div className="col-xs-12 col-md-12 col-lg-6 pr-30 inFront" style={{ minWidth: 710 }}>
              <MasterSelect name="configuration_id" label="Configuration" cols={5} />
              <MasterSelect name="purge_sector_location_id" label="Purge Sector Location" cols={5} />
              {/* Purge Angle disabled while Purge Sector Location == 1 (interlock,
                  energy_wheel.selector.js:2456-2460 / :1555). */}
              <MasterSelect name="purge_angle_id" label="Purge Angle" unit="deg" cols={5}
                disabled={purgeLocked} />
            </div>
            {/* tmpl:929-948 — the configuration picture strip. React had dropped
                the whole column, so `#images_wrapper` (common.css:19480-19535)
                never rendered and the diagrams were gone. */}
            <div className="col-xs-12 col-md-12 col-lg-6" style={{ maxWidth: 'calc(100% - 710px)' }}>
              <ImageStrip
                listId="configuration_images_list"
                tabLabel="Configurations"
                field="configuration_id"
                count={8}
                src={(n) => (isAnnexair
                  ? `/static/images/rotary_htex/configurations/annexair/${n}.png`
                  : `/static/images/rotary_htex/configurations/${n}.png`)}
                order={masterLists.configuration_id ?? []}
                visible={(n) => EW_CONFIG_IMAGES_BY_DIVIDER[configDivider ?? '']?.has(n) ?? true}
                onPick={(id) => onSearchField('configuration_id', String(id))}
              />
            </div>
          </div>

          {/* ----- Mounting (tmpl:950-1039) — hide-for-annexair (whole tab) ----- */}
          {!isAnnexair && (
            <div className={tabClass('Mounting')} id={`${idPrefix}Mounting`}>
              <div className="col-xs-12 col-md-12 col-lg-6 pr-30 inFront">
                <ModelSelect name="fan_locations_id" label="Fan Locations" options={rowsOf(fanLocationsQ)} />
                <DocSelect name="mounting_arr_id" label="Mounting Arrangement" />
                <ModelSelect name="flow_type_id" label="Flow" options={rowsOf(flowTypesQ)} />
                {/* instl_position_id — restored (was fully dropped → data loss) */}
                <MasterSelect name="instl_position_id" label="Installation Position" />
              </div>
              {/* tmpl:1023-1037 — the fan-location picture strip (also dropped).
                  No `visible`/`order`: legacy neither hides nor reorders these
                  three (js:3369-3371 only moves `active`), and the collection is
                  the un-narrowed master list, so the port's option-list hide was
                  a no-op that could only ever have differed from dev. */}
              <div className="col-xs-12 col-md-12 col-lg-6">
                <ImageStrip
                  listId="fan_locations_images_list"
                  tabLabel="Fan Locations"
                  field="fan_locations_id"
                  count={3}
                  src={(n) => `/static/images/rotary_htex/fan_locations/${n}.png`}
                  onPick={(id) => onModelField('fan_locations_id', String(id))}
                />
              </div>
            </div>
          )}

          {/* ----- Options (tmpl:1041-1154) — the ONE pane whose selects sit
              directly in `.input-part > .row`, with no column around them
              (tmpl:1047-1049 and every row after it). See SelectRow. ----- */}
          <div className={tabClass('Options')} id={`${idPrefix}Options`}>
            <div className="col-xs-12 col-md-12 col-lg-6 pr-30">
              <MasterSelect name="temp_control_id" label="Temperature Control" labelKey="name" wrap="bare" />
              <MasterSelect name="rotation_detector_id" label="Rotation Detector" labelKey="name" wrap="bare" />
              {!isAnnexair && <MasterSelect name="coating_frame_id" label="Frame Coating" labelKey="name" wrap="bare" />}
              {!isAnnexair && <MasterSelect name="coating_media_id" label="Media Coating" labelKey="name" wrap="bare" />}
              <MasterSelect name="media_edge_id" label="Media Edge" labelKey="name" wrap="bare" />
              <MasterSelect name="mtr_service_door_id" label="Service Door" labelKey="name" wrap="bare" />
              <MasterSelect name="bottom_plate_id" label="Bottom Plate" labelKey="name" wrap="bare" />
            </div>
          </div>

          {/* ----- Pricing (tmpl:1155-1180 + pricing_method_inputs.tmpl) ----- */}
          <div className={tabClass('Pricing')} id={`${idPrefix}Pricing`}>
            <div className="col-xs-12 col-md-12 col-lg-6 pr-30 float-left">
              {/* tmpl:1163 — the Method select is the only one legacy puts in a
                  `.col-xs-12.right.text-right`, i.e. right-aligned in its column. */}
              <SelectRow label="Method" dataName="pricing_method_id" wrap="right">
                {/* iterate the REAL pricing_methods collection (was hardcoded
                    List/User). */}
                <select
                  name="pricing_method_id"
                  data-name="pricing_method_id"
                  className="pricing_method_field model_field"
                  value={s.pricing_method_id == null ? '' : String(s.pricing_method_id)}
                  onChange={(e) => onModelField('pricing_method_id', e.target.value)}
                >
                  {/* Selector-parity FIX — empty option + List/User fallback
                      (mirror PlateHeatExchangerSelector.tsx:1700-1712) so the
                      Method dropdown is never blank even if the collection
                      fetch is empty/fails (was: bare map, zero options). */}
                  <option value="" />
                  {rowsOf(pricingMethodsQ).length ? (
                    rowsOf(pricingMethodsQ).map((r) => (
                      <option key={String(r.id)} value={String(r.id)} data-value={String((r as any).value ?? r.id)}>
                        {optionLabel(r, 'name')}
                      </option>
                    ))
                  ) : (
                    <>
                      <option value="1">List</option>
                      <option value="2">User</option>
                    </>
                  )}
                </select>
              </SelectRow>
              {/* method != 1 → the input pair. == 2 → user COST; else (net price)
                  → net_price_product_user + net_price_freight_user
                  (pricing_method_inputs.tmpl:13-20). */}
              {s.pricing_method_id != null && Number(s.pricing_method_id) !== 1 && (() => {
                // pricing_method_inputs.tmpl — ONE label, "ER Wheel : Mtl |
                // Freight", over a `four-input-row` holding both amounts side by
                // side. React had rendered two separate full-width rows with
                // their own labels.
                const user = Number(s.pricing_method_id) === 2;
                const prod = user ? 'cost_product_user' : 'net_price_product_user';
                const frt = user ? 'cost_freight_user' : 'net_price_freight_user';
                const money = (name: string, unit?: string) => (
                  <>
                    {unit && <span className="percent ">{unit}</span>}
                    <input type="text" name={name} data-name={name} data-formatter_type="decimal"
                      autoComplete="off" data-min="1" data-max="1000000" data-step="0.01"
                      className="model_field coil_field"
                      {...freeTypingProps(formatDecimalDisplay(s[name]),
                        (raw) => onModelField(name, raw))} />
                  </>
                );
                return (
                  <div className="row pricingInputs">
                    <div className={`col-xs-6 label-part ${s.pricing_method_id}`}>
                      <span data-name={prod} data-name_2={frt}>ER Wheel : Mtl | Freight</span>
                    </div>
                    <div className="col-xs-6 input-part four-input-row">
                      <div className="row">
                        <div className="col-xs-6">{money(prod, 'US$')}</div>
                        <div className="col-xs-6">{money(frt)}</div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
         </div>
        </div>
      </div>

      {/* ===== JOB B — OUTPUT block: a SIBLING of #er-wheel (was nested inside it).
          Legacy classes verbatim from ewheel.selector.tmpl:1184. ===== */}
      {/* ★ `rating` is NOT in that template — legacy's render_outputs adds it
          (`self.$el.find('.output-part').addClass('rating')`, js:3036), so it is
          on this div from the first rated output onwards. Two rules key off it
          and both were dead in the React port: `.output-group.rating.output-content`
          (the sheet's height) and `.rotary-htex .output-group.rating.output-content.coil_new
          .rated_erwheel_model` — the rule that pins the Performance tab's model
          name at 13px/300 in the sheet's top-left corner. Without it that name
          fell back to the inherited body size. */}
      {/* ★ NO `col-xs-12` — it is not in the template either (tmpl:1184), and
          Bootstrap's 15px side padding on it is what offset the WHOLE output
          half 15px right of the input half: the `Output` nav column, every
          output tab label, and the sheet's own label/value columns all started
          one gutter in from their `Input` counterparts, and the sheet was 30px
          narrower than the input pane. Every other rating dialog's output
          container is gutter-less (CoilSelector, PlateHeatExchangerSelector),
          which is why only this one looked indented. Flagged in #1155; the
          rate-in-flight preloader below is already pinned to `.rotary-htex`
          rather than to this div, so nothing else depends on the gutter. */}
      <div
        className={`coil_new output-part rated-item output-group output-content energy_wheel${
          hasRatedOutput && !isRating ? ' rating' : ''
        }`}
      >
        {/* ★ The rated-output SHEET (ewheel.rated.outputs.tmpl). React had a
            generic six-column table (Wheel Size / Eff / SA APD / Ship Wt) with
            no output nav at all; legacy renders THREE output tabs —
            Heat Exchanger (the candidate list: MODEL · REQUIRED AIRWAY W x H ·
            SIZE · DEPTH · FIN PATTERN · FV winter/summer, sortable),
            Performance (the per-season leaving-air sheet) and Notes.
            Gated exactly as legacy gates `render_outputs` (js:597) and as
            CoilSelector does: nothing is drawn until a rate has come back or the
            component already carries a saved wheel. */}
        {hasRatedOutput && !isRating && (
        <EnergyWheelRatedOutputs
          rows={rows}
          ratedIndex={ratedIndex}
          onSelectRow={setRatedIndex}
          state={s}
          airStreams={streams}
          tunnels={ewSections.map((sec) => ({
            index: sec.index,
            aw_width: (sec.aw_width ?? null) as number | null,
            aw_height: (sec.aw_height ?? null) as number | null,
          }))}
          finPatternName={(() => {
            const row = (masterLists.fin_pattern_id ?? []).find(
              (x: any) => String(x.id ?? x.value) === String(s.fin_pattern_id)
            );
            return row ? optionLabel(row, 'name') || null : null;
          })()}
          mfgId={mfg}
          errors={rateErrors}
          warnings={rateWarnings}
          notes={(notesQ.data ?? []) as Array<{ note: string; component_mfg_id?: number }>}
          ratedDatetime={ratedDatetime.current}
          idPrefix={idPrefix}
          notFound={Boolean(ratedRows && !ratedRows.length && !rateErrors.length)}
          inputTabIsPerformance={effTab === 'Performance'}
        />
        )}

      </div>

      {/* ★ THE RATE-IN-FLIGHT PRELOADER — legacy `rate()`
          (energy_wheel.selector.js:2665-2685) does exactly two things to the
          output half before dispatching: empties it (`.output-part.html('')`,
          which is the `!isRating` gate above) and appends
          `<div class="getting-preloader preloader dotted-preloader active">` to
          `.rated-item`.

          `.dotted-preloader` is `position:absolute` with `top/left:0` and
          `width/height:100%` (common.css:29980), and
          `.ahu-rate-content-item .dotted-preloader` forces it opaque at
          `z-index:11` (:13963) — so left alone it covers whatever its containing
          block is. What confines it to the output half is ONE rule, keyed on the
          `getting-preloader` class and measured from the `.rotary-htex` wrapper:

            #rating_mode_energy_wheel_content .getting-preloader {
              height: calc(100% - 397px);  top: 397px;            (:19061)
            }

          The port rendered `er-wheel-start-preloader` instead — legacy's
          INITIAL-RENDER preloader (ewheel.selector.tmpl:1, re-armed for the
          header restore at js:1396), which is meant to cover everything. No
          stylesheet selects on that class, so the overlay kept its full-wrapper
          box and hid the entire Input table for the length of the vendor call.
          A silent legacy-selector miss: right element, wrong class, no error.

          ★ It is rendered as a child of the WRAPPER rather than of `.rated-item`
          because :19061's 397px offset is only correct when it resolves against
          `.rotary-htex`. That is what legacy gets for free — its `.rated-item`
          is `position:static`, so an absolute child escapes to the wrapper
          (Bootstrap's `.col-*` are position:relative) — but this port still
          carries a stray `col-xs-12` on the output div, which would capture it
          and squeeze the overlay into a ~94px strip at the pane's foot. Pinning
          it to the wrapper reproduces legacy's RESOLVED geometry either way, and
          both rules that style it (:19061, :13963) are descendant selectors that
          match from here unchanged. `#1155` removes that `col-xs-12` as its own
          parity fix; this stays correct before and after it lands. */}
      {isRating && <div className="getting-preloader preloader dotted-preloader active" />}
    </div>
    </EwCtx.Provider>
  );
};

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

/** divider derivation from the tunnel configuration (legacy getDividerFromTunnel). */
function dividerFromTunnel(cfg: string | null | undefined): string | null {
  if (!cfg) return null;
  const parts = String(cfg).split('_');
  if (parts.length !== 2) return null;
  const rows = parseInt(parts[0], 10);
  const cols = parseInt(parts[1], 10);
  if (Number.isNaN(rows) || Number.isNaN(cols)) return null;
  if (cols > rows) return 'horizontal';
  if (rows > cols) return 'vertical';
  return null;
}

/**
 * Which configuration diagrams each tunnel divider keeps
 * (energy_wheel.selector.js:1478-1481). The same split the server applies to the
 * dropdown — `ksdm_master_ahu_energy_wheel_configuration.divider_id` is Vertical
 * for 1/4/5/8 and Horizontal for 2/3/6/7 — hardcoded there, and left hardcoded
 * here so the tiles cannot drift out of step with the option list mid-fetch. An
 * unlisted divider (or none) hides nothing.
 */
const EW_CONFIG_IMAGES_BY_DIVIDER: Record<string, Set<number>> = {
  vertical: new Set([1, 4, 5, 8]),
  horizontal: new Set([2, 3, 6, 7]),
};

/** master-list fallback (when a field's own list isn't cached, return []). */
function graphRowsFallback(_name: string, _lists: Record<string, OptionRow[]>): OptionRow[] {
  return [];
}

/**
 * The `value` legacy puts on a master-list <option>:
 * `self.eachModel[item].id || self.eachModel[item]` (energy_wheel.selector.js:3316).
 * Most lists are row objects, but `speed` is a flat array of raw numbers — those
 * have neither `.id` nor `.value`, and `String(r.id ?? r.value)` turned every
 * Speed option into `value="undefined"` with an empty label.
 */
function ewRowId(r: unknown): string {
  if (r != null && typeof r === 'object') {
    const row = r as Record<string, unknown>;
    return String(row.id ?? row.value);
  }
  return String(r);
}

/**
 * Build the selectErWheel WRITE payload (§33.3 models_field + params_field).
 * Pure so the verifier can read the exact write shape. models_field is what
 * current_model.set receives (auto-saves); params_field is JSON-stringified into
 * models_field.energy_wheel_params.
 */
/**
 * Selector-parity — `rating_warnings` (ewheel.selector.js:1875-1879).
 * Legacy flattens the chosen rated row's `errors` into `"<key>:<text>;"`
 * pairs with a `for…in`, so the key is the array index for a list and the
 * property name for an object; both shapes round-trip through the reader's
 * `.split(';')` at :2824. Empty input ⇒ `""`, matching the initialiser.
 */
export function ewRatingText(errors: unknown): string {
  if (errors == null) return '';
  let out = '';
  for (const k of Object.keys(errors as object)) out += `${k}:${(errors as any)[k]};`;
  return out;
}

/**
 * Selector-parity — `rating_notes` (ewheel.selector.js:1880-1884). Same shape,
 * one level deeper: `notes` is a list of objects and legacy emits the INNER
 * key, discarding the outer index — so two notes objects sharing a key both
 * appear. Reproduced verbatim; the reader at :2852 splits on ';' either way.
 */
export function ewRatingNotesText(notes: unknown): string {
  if (notes == null) return '';
  let out = '';
  for (const i of Object.keys(notes as object)) {
    const inner = (notes as any)[i];
    if (inner == null || typeof inner !== 'object') continue;
    for (const j of Object.keys(inner)) out += `${j}:${inner[j]};`;
  }
  return out;
}

export function buildSelectPayload(
  s: Record<string, any>,
  r: Record<string, any>,
  masterLists: Record<string, OptionRow[]>,
  /** the `.model_field` sweep above, merged in at exactly the point legacy's
   * loop sits — after the literal, before the missing-season nulling. */
  sweptFields: Record<string, number> = {}
): { modelsField: Record<string, unknown>; paramsField: Record<string, unknown> } {
  const mfg = Number(s.mfg_id);
  const flr = (v: any) => (v == null || v === '' ? v : Math.floor(Number(v)));
  // fin_pattern NAME from the option attr (data-item-name) — read from the
  // master list row for the selected fin_pattern_id.
  const finRow = (masterLists.fin_pattern_id ?? []).find((x: any) => String(x.id) == String(s.fin_pattern_id));
  const finPatternName = (finRow as any)?.name ?? (finRow as any)?.value ?? '';

  const paramsField: Record<string, unknown> = {
    frost_control_winter: r.frost_control_winter,
    shipping_weight: +r.shipping_weight || undefined,
    operating_weight: +r.operating_weight || undefined,
    mfg_id: +s.mfg_id,
    series_id: +s.series_id,
    fin_pattern_id: +s.fin_pattern_id,
    type_id: +s.type_id,
    speed_control_id: +s.speed_control_id,
    speed: +s.speed,
    mtr_voltage_id: +s.mtr_voltage_id,
    mounting_arr_id: +s.mounting_arr_id,
    purge_angle_id: +s.purge_angle_id,
    desiccant_id: +s.desiccant_id,
    master_energy_wheel_id: +r.id || undefined,
    // `--` (null) must round-trip as null, not as `+null === 0`: the params blob
    // is what re-seeds the dialog (`rotor_style_id: p.rotor_style_id`), and a 0
    // matches no master row, so the select would reopen on a bare "0" option
    // with no way back to the dash. Same null-guard as `instl_position_id`.
    rotor_style_id: s.rotor_style_id != null ? +s.rotor_style_id : undefined,
    rotor_depth_id: +s.rotor_depth_id || r.rotor_depth_id,
    rotor_depth: r.rotor_depth,
    wheel_size_id: +s.wheel_size_id || r.wheel_size,
    wheel_size: +s.wheel_size_id || r.wheel_size,
    seal_id: +s.seal_id,
    drive_belt_id: +s.drive_belt_id,
    fin_thickness_id: +s.fin_thickness_id,
    well_height_id: +s.well_height_id,
    hub_id: +s.hub_id,
    wheel_height_id: +s.wheel_height_id,
    casing_id: +s.casing_id,
    casing_type_id: +s.casing_type_id,
    purge_sector_location_id: +s.purge_sector_location_id,
    // Selector parity — instl_position_id data-loss fix: persist it in the params
    // blob so it round-trips (legacy renders/seeds it but its explicit params_field
    // omits it; React restores full persistence per the parity brief). null-safe:
    // `+undefined` → NaN, so guard with the raw value.
    instl_position_id: s.instl_position_id != null ? +s.instl_position_id : undefined,
    temp_control_id: +s.temp_control_id,
    rotation_detector_id: +s.rotation_detector_id,
    coating_frame_id: +s.coating_frame_id,
    media_edge_id: +s.media_edge_id,
    mtr_service_door_id: +s.mtr_service_door_id,
    bottom_plate_id: +s.bottom_plate_id,
    coating_media_id: +s.coating_media_id,
    configuration_id: +s.configuration_id,
    fin_pattern: finPatternName,
    min_face_velocity: +s.min_face_velocity,
    max_face_velocity: +s.max_face_velocity,
    // Heatex-E (mfg 52 / series 274) forces eatr_oacf on (updateOacfControl,
    // energy_wheel.selector.js:2418-2432 → params_field.eatr_oacf, js:2067).
    eatr_oacf:
      Number(s.mfg_id) === 52 && Number(s.series_id) === 274 ? true : s.eatr_oacf,
    altitude: s.elevation,
  };

  const modelsField: Record<string, unknown> = {
    // summer oa/sa/ra/ea (from the rated row)
    oa_air_stream_summer: r.oa_air_stream_summer,
    oa_airflow_actual_summer: r.oa_airflow_actual_summer,
    oa_airflow_std_summer: r.oa_airflow_std_summer,
    oa_db_temp_summer: r.oa_db_temp_summer,
    oa_wb_temp_summer: r.oa_wb_temp_summer,
    oa_rh_summer: r.oa_rh_summer,
    oa_hr_summer: r.oa_hr_summer,
    oa_w_summer: r.oa_w_summer,
    sa_airflow_actual_summer: r.sa_airflow_actual_summer,
    sa_airflow_std_summer: r.sa_airflow_std_summer,
    sa_db_temp_summer: r.sa_db_temp_summer,
    sa_wb_temp_summer: r.sa_wb_temp_summer,
    sa_rh_summer: r.sa_rh_summer,
    sa_hr_summer: r.sa_hr_summer,
    sa_w_summer: r.sa_w_summer,
    sa_apd_summer: r.sa_apd_summer,
    sa_face_velocity_summer: flr(r.sa_face_velocity_summer),
    ra_air_stream_summer: r.ra_air_stream_summer,
    ra_airflow_actual_summer: r.ra_airflow_actual_summer,
    ra_airflow_std_summer: r.ra_airflow_std_summer,
    ra_db_temp_summer: r.ra_db_temp_summer,
    ra_wb_temp_summer: r.ra_wb_temp_summer,
    ra_rh_summer: r.ra_rh_summer,
    ra_hr_summer: r.ra_hr_summer,
    ra_w_summer: r.ra_w_summer,
    ra_apd_summer: r.ra_apd_summer,
    ea_airflow_actual_summer: r.ea_airflow_actual_summer,
    ea_airflow_std_summer: r.ea_airflow_std_summer,
    ea_db_temp_summer: r.ea_db_temp_summer,
    ea_wb_temp_summer: r.ea_wb_temp_summer,
    ea_rh_summer: r.ea_rh_summer,
    ea_hr_summer: r.ea_hr_summer,
    ea_w_summer: r.ea_w_summer,
    ea_apd_summer: r.ea_apd_summer,
    ea_face_velocity_summer: flr(r.ea_face_velocity_summer),
    sa_air_stream_summer: r.sa_air_stream_summer,
    ea_air_stream_summer: r.ea_air_stream_summer,
    unit_effectiveness_summer: r.unit_effectiveness_summer,
    sensible_efficiency_summer: r.sensible_efficiency_summer,
    sensible_capacity_summer: flr(r.sensible_capacity_summer),
    latent_efficiency_summer: r.latent_efficiency_summer,
    latent_capacity_summer: flr(r.latent_capacity_summer),
    total_efficiency_summer: r.total_efficiency_summer,
    total_capacity_summer: flr(r.total_capacity_summer),
    rer_summer: r.rer_summer,
    eatr_summer: r.eatr_summer,
    oacf_summer: r.oacf_summer,
    // winter
    oa_air_stream_winter: r.oa_air_stream_winter,
    oa_airflow_actual_winter: +s.oa_airflow_actual_winter,
    oa_airflow_std_winter: +s.oa_airflow_std_winter,
    oa_wb_temp_winter: r.oa_wb_temp_winter,
    oa_rh_winter: r.oa_rh_winter,
    oa_hr_winter: r.oa_hr_winter,
    oa_w_winter: r.oa_w_winter,
    sa_airflow_actual_winter: r.sa_airflow_actual_winter,
    sa_airflow_std_winter: r.sa_airflow_std_winter,
    sa_db_temp_winter: r.sa_db_temp_winter,
    sa_wb_temp_winter: r.sa_wb_temp_winter,
    sa_rh_winter: r.sa_rh_winter,
    sa_hr_winter: r.sa_hr_winter,
    sa_w_winter: r.sa_w_winter,
    sa_apd_winter: r.sa_apd_winter,
    sa_face_velocity_winter: flr(r.sa_face_velocity_winter),
    ra_air_stream_winter: r.ra_air_stream_winter,
    ra_airflow_actual_winter: r.ra_airflow_actual_winter,
    ra_airflow_std_winter: r.ra_airflow_std_winter,
    ra_db_temp_winter: r.ra_db_temp_winter,
    ra_wb_temp_winter: r.ra_wb_temp_winter,
    ra_rh_winter: r.ra_rh_winter,
    ra_hr_winter: r.ra_hr_winter,
    ra_w_winter: r.ra_w_winter,
    ra_apd_winter: r.ra_apd_winter,
    ea_airflow_actual_winter: r.ea_airflow_actual_winter,
    ea_airflow_std_winter: r.ea_airflow_std_winter,
    ea_db_temp_winter: r.ea_db_temp_winter,
    ea_wb_temp_winter: r.ea_wb_temp_winter,
    ea_rh_winter: r.ea_rh_winter,
    ea_hr_winter: r.ea_hr_winter,
    ea_w_winter: r.ea_w_winter,
    ea_apd_winter: r.ea_apd_winter,
    ea_face_velocity_winter: flr(r.ea_face_velocity_winter),
    sa_air_stream_winter: r.sa_air_stream_winter,
    ea_air_stream_winter: r.ea_air_stream_winter,
    unit_effectiveness_winter: flr(r.unit_effectiveness_winter),
    sensible_efficiency_winter: flr(r.sensible_efficiency_winter),
    sensible_capacity_winter: flr(r.sensible_capacity_winter),
    latent_efficiency_winter: flr(r.latent_efficiency_winter),
    latent_capacity_winter: flr(r.latent_capacity_winter),
    total_efficiency_winter: r.total_efficiency_winter,
    total_capacity_winter: flr(r.total_capacity_winter),
    rer_winter: r.rer_winter,
    condensate_winter: r.condensate_winter,
    eatr_winter: r.eatr_winter,
    oacf_winter: r.oacf_winter,
    // controls + metadata
    flow_type_id: +s.flow_type_id,
    fan_locations_id: +s.fan_locations_id,
    speed_control_id: +s.speed_control_id,
    speed: +s.speed,
    min_face_velocity: +s.min_face_velocity,
    max_face_velocity: +s.max_face_velocity,
    rotor_depth_id: +s.rotor_depth_id || r.rotor_depth_id,
    pricing_method_id: s.pricing_method_id,
    cost_product_user: s.cost_product_user,
    cost_freight_user: s.cost_freight_user,
    net_price_product_user: s.net_price_product_user,
    net_price_freight_user: s.net_price_freight_user,
    generic_mfg: s.__generic_mfg ?? false,
    master_energy_wheel_id: +r.id || undefined,
    shipping_weight: r.shipping_weight,
    difference_in_pressure_winter: r.difference_in_pressure_winter,
    difference_in_pressure_summer: r.difference_in_pressure_summer,
    // ★ `s`, not `r`: `__rated_datetime` is injected onto the FIRST argument
    // at the call site, so reading it off the rated row made this column
    // unconditionally null on every React-selected wheel (legacy
    // `'rated_datetime': self.rated_datetime`, ewheel.selector.js:1992).
    rated_datetime: s.__rated_datetime ?? null,
    // Restored — the rating audit trail (ewheel.selector.js:1875-1883/:1993-1994).
    // Both are `varchar(2000)` columns on AHUEnergyWheel and writable serializer
    // fields; the port sent neither, so the Warnings / Rating Notes panels came
    // back empty on reopen. Legacy's exact `"<key>:<text>;"` concatenation is
    // reproduced in ewRatingText/ewRatingNotesText so what is written parses
    // back through the same `.split(';')` readers (:2824/:2852).
    rating_warnings: ewRatingText(r.errors),
    rating_notes: ewRatingNotesText(r.notes),
  };

  // Legacy's `.model_field` sweep sits HERE — after the literal, before the
  // blob stringify and the season nulling (ewheel.selector.js:2054-2066) — and
  // it OVERRIDES the literal (`models_field[i] = +value`). That matters for the
  // entering conditions: the rated row echoes back the leaving side and returns
  // null for `oa_rh_*` / `ra_db_temp_*` / `ra_rh_*` / `ra_airflow_actual_*`, so
  // preferring the literal's null over the value on screen wrote the user's own
  // entering air conditions away on Select, and the reopened dialog came back
  // with an empty Humidity. See modelFieldSweep.ts for the dev row this was
  // measured against.
  mergeSweptFields(modelsField, sweptFields, true);

  // params ride inside the JSON blob (legacy line 2003).
  modelsField.energy_wheel_params = JSON.stringify(paramsField);

  // missing-season nulling (legacy 2004-2013): blank season ⇒ null its fields
  // EXCEPT the air-stream ids.
  if (!modelsField.oa_airflow_actual_winter) {
    (WINTER_REQUIRED_FIELDS_FOR_RATE[mfg] ?? []).forEach((k) => {
      if (k !== 'oa_air_stream_winter_id' && k !== 'ra_air_stream_winter_id') modelsField[k] = null;
    });
  }
  if (!modelsField.oa_airflow_actual_summer) {
    (SUMMER_REQUIRED_FIELDS_FOR_RATE[mfg] ?? []).forEach((k) => {
      if (k !== 'oa_air_stream_summer_id' && k !== 'ra_air_stream_summer_id') modelsField[k] = null;
    });
  }
  return { modelsField, paramsField };
}

// ---------------------------------------------------------------------------
// small presentational helper (matches the W2 Row)
// ---------------------------------------------------------------------------
// doc select (mfg/series/mounting/voltage).
const DocSelect = ({ name, label, cols }: { name: string; label: string; cols?: 4 | 5 | 6 }) => {
  const { s, ewDoc, optionIds, onSearchField } = useEw();
  const value = s[name];
  const valStr = value == null || value === '' ? '' : String(value);
  // Legacy builds a doc-select's options from the dependency graph's NARROWED ids
  // resolved against the CMS doc — `get_from_cms_data(path,
  // parameterPossibleIDs.get(key).data, erWheelData)` (mergeIdsWithCMS,
  // energy_wheel.selector.js:1204-1206). The port rendered the FULL doc list
  // instead, so the Series select offered every manufacturer's series: picking
  // Heatex's `E` under AI Rotor sent `?mfg_id=233&series_id=274` down the whole
  // cascade, which the server answers `[]` — the same dead end `loadParams`
  // already narrows the ID LIST to avoid (EW_NARROWED_DOC_FIELDS).
  //
  // Only the narrowed fields, and only once the graph has a NON-EMPTY list:
  // `mounting_arr_id` narrows to `[]` for most manufacturers and an empty list
  // here would blank a select that renders today, which is a separate call from
  // the series fix. Until then it keeps the full doc list it has always had.
  const narrowed = EW_NARROWED_DOC_FIELDS.has(name) ? optionIds(name) : null;
  const rows = ewCmsOptionList(
    ewDoc as EnergyWheelCmsDoc, name, narrowed && narrowed.length ? narrowed : undefined);
  const hasRow = rows.some((r) => String(r.id) === valStr);
  const selLabel = valStr ? ewCmsSelectedLabel(ewDoc as EnergyWheelCmsDoc, name, value as any) : '';
  return (
    <SelectRow label={label} dataName={name} cols={cols}>
      <select
        name={name}
        data-name={name}
        className="search_field"
        value={valStr}
        onChange={(e) => onSearchField(name, e.target.value)}
      >
        {ewShowsDash(name, valStr) && (
          <option value="">{EW_DASH_DEFAULT_FIELDS.has(name) ? '--' : ''}</option>
        )}
        {rows.map((r) => (
          <option key={String(r.id)} value={String(r.id)} data-item-name={String(r.label ?? '')}
            data-value={String(r.id)} data-generic={String((r as any).generic_mfg ?? 'none')}>
            {String(r.label ?? '')}
          </option>
        ))}
        {valStr !== '' && !hasRow && <option value={valStr}>{selLabel || valStr}</option>}
      </select>
    </SelectRow>
  );
};

// master-list select (type/rotor/casing/purge/coating/etc.). `unit` renders the
// right-hand unit label (in/deg); `disabled` drives the legacy `non-clickable`
// interlock (e.g. Purge Angle while Purge Sector Location == 1).
const MasterSelect = ({
  name, label, labelKey, unit, disabled, cols, wrap,
}: {
  name: string; label: string; labelKey?: string; unit?: string;
  disabled?: boolean; cols?: 4 | 5 | 6; wrap?: 'col12' | 'bare' | 'right';
}) => {
  const { s, masterLists, onSearchField } = useEw();
  const value = s[name];
  const valStr = value == null || value === '' ? '' : String(value);
  const rows = masterLists[name] ?? graphRowsFallback(name, masterLists);
  const hasRow = rows.some((r: any) => ewRowId(r) === valStr);
  // Legacy renderSelects (energy_wheel.selector.js:3288) picks each option's
  // TEXT as `name ? name : value` — for EVERY select, never value-first. The
  // port defaulted `labelKey` to 'value', so every master row carrying both
  // rendered its slug/raw number instead of its label: Fin Thickness showed
  // "standard / f70 / f100 / 0.002" where dev shows "Standard / f70 / f100 /
  // .002", and Well Height showed "1.5 / 1.65 / 1.7 / 2 / 2.7" where dev shows
  // "XL - 1.50 / SL - 1.65 / 1.70 / 2.00 / 2.70". Dropping the default falls
  // through to optionLabel's name → value → series chain, which is legacy's
  // rule including its `|| .series` fall-through (js:3316).
  const optLabel = (r: any): string => {
    // js:3294 — `speed`'s option list is a flat array of NUMBERS
    // (rest_cms/energy_wheels_viewset.py:622-627 responds with bare speeds),
    // so the rows have no .name/.value at all and rendered BLANK. Legacy
    // prints Number(x).toFixed(2) → "25.00".
    if (name === 'speed') return Number(ewRowId(r)).toFixed(2);
    // js:3297 — Rotor Size is Math.round(value); the column is a float.
    if (name === 'wheel_size_id') return String(Math.round(Number(r?.value ?? r)));
    return optionLabel(r, labelKey) || optionLabel(r);
  };
  return (
    <SelectRow label={label} dataName={name} unit={unit} cols={cols} wrap={wrap}>
      <select
        name={name}
        data-name={name}
        className={`search_field${disabled ? ' non-clickable' : ''}`}
        disabled={disabled}
        value={valStr}
        onChange={(e) => onSearchField(name, e.target.value)}
      >
        {ewShowsDash(name, valStr) && (
          <option value="">{EW_DASH_DEFAULT_FIELDS.has(name) ? '--' : ''}</option>
        )}
        {rows.map((r: any) => (
          <option key={ewRowId(r)} value={ewRowId(r)}
            data-item-name={optLabel(r)} data-value={ewRowId(r)}>
            {optLabel(r)}
          </option>
        ))}
        {valStr !== '' && !hasRow && <option value={valStr}>{valStr}</option>}
      </select>
    </SelectRow>
  );
};

const ModelSelect = ({ name, label, options, cols }: {
  name: string; label: string; options: OptionRow[]; cols?: 4 | 5 | 6;
}) => {
  const { s, onModelField } = useEw();
  const value = s[name];
  const valStr = value == null || value === '' ? '' : String(value);
  const hasRow = options.some((r) => String(r.id) === valStr);
  return (
    <SelectRow label={label} dataName={name} cols={cols}>
      <select name={name} data-name={name} className="model_field" value={valStr}
        onChange={(e) => onModelField(name, e.target.value)}>
        {ewShowsDash(name, valStr) && (
          <option value="">{EW_DASH_DEFAULT_FIELDS.has(name) ? '--' : ''}</option>
        )}
        {options.map((r) => (
          <option key={String(r.id)} value={String(r.id)}>{optionLabel(r, 'name')}</option>
        ))}
        {valStr !== '' && !hasRow && <option value={valStr}>{valStr}</option>}
      </select>
    </SelectRow>
  );
};

/**
 * The picture strip legacy renders beside the Configuration and Mounting
 * fields (tmpl:929-948 / :1023-1037). `configuration_list`/`locations_list`
 * are just 1..8 and 1..3 (energy_wheel.selector.js:1537-1538); a tile gets
 * `active` when it matches the selected value (js:2462/:3357). The whole
 * column was missing from the React port.
 *
 * `visible` is the per-strip hide rule — legacy has ONE, and it is
 * `filterConfigurationImages` (js:1475-1493) on the configuration strip only,
 * keyed off the tunnel divider rather than off the option list. `order` is
 * legacy's re-append of every tile the dropdown names, in dropdown order
 * (js:3358-3368): tiles the list does not name keep their place ahead of them.
 */
const ImageStrip = ({ listId, tabLabel, field, count, src, order, visible, onPick }: {
  listId: string; tabLabel: string; field: string; count: number;
  src: (n: number) => string; order?: OptionRow[]; visible?: (n: number) => boolean;
  onPick: (id: number) => void;
}) => {
  const { s } = useEw();
  const sel = s[field];
  const all = Array.from({ length: count }, (_, i) => i + 1);
  const ranked = (order ?? [])
    .map((r) => Number((r as any).id ?? (r as any).value ?? r))
    .filter((n) => all.includes(n));
  const nums = ranked.length
    ? [...all.filter((n) => !ranked.includes(n)), ...ranked]
    : all;
  return (
    <div id="images_wrapper">
      <div className="configuration_images_label">
        <div data-id={listId} data-select={field} className="img-tab active">{tabLabel}</div>
      </div>
      <div id={listId} className="images_list active">
        {nums.map((n) => (
          <div key={n} data-image={n}
            className={`configuration_image${String(sel) === String(n) ? ' active' : ''}`}
            style={visible && !visible(n) ? { display: 'none' } : undefined}
            onClick={() => onPick(n)}>
            <img src={src(n)} />
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * One PAIRED Performance row — legacy puts OA and RA side by side under ONE
 * shared `col-xs-4 label-part right-row` label, with the two controls in
 * `col-xs-6` halves of a `col-xs-8 input-part` (ewheel.selector.tmpl:377-615
 * summer / :634-873 winter). React had split every pair into two full-width
 * `col-xs-5/7` rows, which doubled the pane height, dropped the ENTERING
 * SUPPLY AIR / ENTERING EXHAUST AIR column headings and lost the labels
 * ("OA Airflow"/"RA Airflow" instead of one "Airflow").
 */
const PerfRow = ({ label, dataName, dataName2, rowClass, labelClass, labelStyle,
  inputPart = true, wrapRow = true, children }: {
  label: React.ReactNode; dataName: string; dataName2?: string;
  rowClass?: string; labelClass?: string; labelStyle?: React.CSSProperties;
  inputPart?: boolean; wrapRow?: boolean; children: React.ReactNode;
}) => {
  const { s } = useEw();
  // legacy `checkAndUpdateRequiredFields` (js:2619-2640) drives `.required` on
  // THIS element; the template's own `required` (only Air Pressure carries one,
  // tmpl:528) is just the pre-cascade state it immediately overwrites.
  const req = ewLabelIsRequired(s.mfg_id, dataName, dataName2);
  const cls = ['col-xs-4 label-part right-row', labelClass, req ? 'required' : null]
    .filter(Boolean).join(' ');
  return (
  <div className={rowClass ? `row ${rowClass}` : 'row'}>
    <div className={cls}
      style={labelStyle} data-name={dataName} data-name_2={dataName2}>
      {label}
    </div>
    {/* tmpl:566-580 — the EATR-OACF row's second cell is a bare col-xs-8 with
        NO `input-part`; every other row has it. */}
    <div className={`col-xs-8${inputPart ? ' input-part' : ''}`}>
      {/* Every row wraps its halves in one `.row`, EXCEPT the WB/RH toggle row,
          whose input-part holds `.row.wb-group` / `.row.rh-group` directly
          (tmpl:463-500). `.component-rating-mode .row` is margin -15px each
          side, so an extra wrapper widens the two `.col-xs-6` halves by 15px
          apiece and the 75% inputs inside them with it — 324px everywhere else
          against 335px here. `wrapRow={false}` keeps the nesting depth legacy's. */}
      {wrapRow ? <div className="row">{children}</div> : children}
    </div>
  </div>
  );
};

/**
 * `checkAndUpdateRequiredFields` (energy_wheel.selector.js:2619-2640) — the
 * REQUIRED-FIELD ASTERISK. Legacy walks `self.inputsDOM` (built by
 * `getTreeOfAllInputs`, js:2500, from `input[name], select[name],
 * span[data-name], div[data-name]`) and, for every entry the current
 * manufacturer's `REQUIRED_FIELDS` set names, adds `.required` to that entry's
 * LABEL element — but only `if (inputsDOM[name].input.localName !== 'select')`.
 * Everything else has `.required` removed.
 *
 * The port dropped the whole routine, so only the two labels the template ships
 * with a literal `required` (WB Temperature's inner span, tmpl:439, and the Air
 * Pressure row, tmpl:528) ever showed an asterisk. Airflow and DB Temperature
 * are `EW_REQUIRED_60` members backed by plain `<input>`s, so dev marks them and
 * dev3 did not.
 *
 * Two details that keep this faithful rather than "mark everything required":
 *  · the `localName !== 'select'` test is why **Air Stream** stays unmarked even
 *    though `oa_air_stream_<season>_id` IS in the set — every `<select>` in
 *    ewheel.selector.tmpl is named `*_id` bar `speed`, so that is the test here.
 *  · `elevation` is in the set but `getTreeOfAllInputs` deletes `altitude` (and,
 *    in header mode, `elevation`) from `inputsDOM`, so Altitude is never marked
 *    on dev either — a dead set member, the class FINDINGS calls out for the fan
 *    selector. It is not a `label-part` we render a PerfRow for, so it stays out
 *    by construction.
 */
const ewLabelIsRequired = (
  mfgId: unknown,
  ...names: Array<string | undefined>
): boolean => {
  const req = EW_REQUIRED_FIELDS[Number(mfgId)];
  if (!req) return false;
  return names.some(
    (n) => !!n && !(n.endsWith('_id') || n === 'speed') && req.has(n)
  );
};

/**
 * Legacy's explicit Performance-tab tab order (ewheel.selector.tmpl:384-598
 * summer / :642-856 winter). It walks the ENTERING SUPPLY AIR column top to
 * bottom, THEN the ENTERING EXHAUST AIR column top to bottom — not left/right
 * across each row, which is what plain DOM order gives you. Only this tab
 * carries `tabindex`; every other pane is left in document order.
 *
 *   summer base 12 · winter base 25, and RA = OA + 5:
 *     +0 air stream   +1 airflow   +2 db/wb/rh   +4 static pressure
 *     +10 pressure difference   +11 min face vel   +12 max face vel
 *
 * The three temperature rows deliberately SHARE one index (14/19, 27/32): they
 * are mutually exclusive per manufacturer (`show-for-heatex` vs the WB/RH
 * toggle's hidden group), and equal indices fall back to DOM order anyway.
 * `+3` and `+8` are unused in the template — gaps kept so the numbers match it
 * line for line. `eatr_oacf` is 22 in BOTH seasons (tmpl:574 and :830); that is
 * legacy's own copy-paste, not a typo here, and it is invisible in practice
 * because only one season pane is ever displayed.
 */
const perfTab = (sea: SeasonTab, offset: number): number => (sea === 'summer' ? 12 : 25) + offset;

/** a paired text input half (tmpl:401-418 etc.) */
const PerfNum = ({ name, unit, unitClass = 'percent', formatter = 'decimal', model = true,
  inputClass = '', readOnly, tabIndex }: {
  name: string; unit?: string; unitClass?: string; formatter?: 'integer' | 'decimal';
  model?: boolean; inputClass?: string; readOnly?: boolean; tabIndex?: number;
}) => {
  const { s, onModelField, onSearchField } = useEw();
  const v = s[name];
  const fmt = formatter === 'integer' ? formatIntegerDisplay : formatDecimalDisplay;
  const display = fmt(v);
  return (
    <>
      <span className={unitClass}>{unit ?? ''}</span>
      <input type="text" name={name} data-name={name} data-formatter_type={formatter}
        autoComplete="off"
        className={`${model ? 'model_field' : 'search_field'}${inputClass ? ` ${inputClass}` : ''}${readOnly ? ' non-clickable' : ''}`}
        readOnly={readOnly} disabled={readOnly} tabIndex={readOnly ? -1 : tabIndex}
        {...freeTypingProps(
          display,
          (raw) => (model ? onModelField(name, raw) : onSearchField(name, raw)),
          // onModelField keeps a non-`_id` field's RAW string, onSearchField
          // stores num(raw) — mirror each so the box shows what the next render
          // would have shown. See freeTypingProps' `reformat` note.
          (raw) => fmt(model ? raw : num(raw)),
        )} />
    </>
  );
};

/** a paired air-stream select half (tmpl:381-395) */
const PerfStream = ({ name, selectClass, tabIndex }: {
  name: string; selectClass: string; tabIndex?: number;
}) => {
  const { s, streams, onModelField } = useEw();
  const value = s[name];
  const valStr = value == null || value === '' ? '' : String(value);
  const hasRow = streams.some((r) => String(r.id) === valStr);
  return (
    <select name={name} data-name={name} className={`model_field ${selectClass}`} value={valStr}
      tabIndex={tabIndex}
      onChange={(e) => onModelField(name, e.target.value)}>
      {valStr === '' && <option value="" />}
      {streams.map((r) => (
        <option key={String(r.id)} value={String(r.id)}>{optionLabel(r, 'name')}</option>
      ))}
      {valStr !== '' && !hasRow && <option value={valStr}>{valStr}</option>}
    </select>
  );
};

// ---- one season's stream block (oa/sa + ra/ea), WB/RH toggled ----
const SeasonBlock = ({ sea }: { sea: SeasonTab }) => {
  const {
    s, mfg, season, isWbTemp, isHeatexE, isAnnexair, onSearchField, onCheckboxField, onWbRhToggle,
  } = useEw();
  // tmpl:437 `show-for-heatex` — the standalone WB row, shown only for Heatex
  // (mfg 52); the toggle row below covers the WBRH_TOGGLE_MFG_IDS mfgs
  // (energy_wheel.selector.js:1500/:1504 showHideInputsDependsOnMfg).
  const showHeatexWb = mfg === 52;
  const hasWbRhToggle = WBRH_TOGGLE_MFG_IDS.has(mfg);
  // both panes always carry their id (tmpl:359 `#summer` / :617 `#winter`);
  // only `in active` moves with the season.
  return (
    <div id={sea} className={`${sea} tab-pane fade${season === sea ? ' in active' : ''}`}>
      {/* tmpl:360-375 — the two column headings over the OA/RA halves. */}
      <div className="row">
        <div className="col-xs-offset-4 col-xs-8 input-part">
          <div className="row subtitles">
            <div className="col-xs-3 output-data">
              <div className="row"><span className="output-without-unit">ENTERING SUPPLY AIR</span></div>
            </div>
            <div className="col-xs-3 col-xs-offset-3 output-data">
              <div className="row"><span className="output-without-unit">ENTERING EXHAUST AIR</span></div>
            </div>
          </div>
        </div>
      </div>

      {/* Air Stream (tmpl:377-396) */}
      <PerfRow label="Air Stream"
        dataName={`oa_air_stream_${sea}_id`} dataName2={`ra_air_stream_${sea}_id`}>
        <div className="col-xs-6">
          <PerfStream name={`oa_air_stream_${sea}_id`} selectClass="col-xs-12 right"
            tabIndex={perfTab(sea, 0)} />
        </div>
        <div className="col-xs-6 text-right">
          <PerfStream name={`ra_air_stream_${sea}_id`} selectClass="col-xs-10 right"
            tabIndex={perfTab(sea, 5)} />
        </div>
      </PerfRow>

      {/* Airflow (tmpl:397-417) */}
      <PerfRow label="Airflow"
        dataName={`oa_airflow_actual_${sea}`} dataName2={`ra_airflow_actual_${sea}`}>
        <div className="col-xs-6">
          <PerfNum name={`oa_airflow_actual_${sea}`} unit="CFM" formatter="integer" inputClass="col-xs-12"
            tabIndex={perfTab(sea, 1)} />
        </div>
        <div className="col-xs-6">
          <PerfNum name={`ra_airflow_actual_${sea}`} formatter="integer" inputClass="right col-xs-12"
            tabIndex={perfTab(sea, 6)} />
        </div>
      </PerfRow>

      {/* DB Temperature (tmpl:418-436) */}
      <PerfRow label="DB Temperature"
        dataName={`oa_db_temp_${sea}`} dataName2={`ra_db_temp_${sea}`}>
        <div className="col-xs-6">
          <PerfNum name={`oa_db_temp_${sea}`} unit="F" unitClass="left percent temp"
            tabIndex={perfTab(sea, 2)} />
        </div>
        <div className="col-xs-6">
          <PerfNum name={`ra_db_temp_${sea}`} unitClass="left percent" inputClass="col-xs-10"
            tabIndex={perfTab(sea, 7)} />
        </div>
      </PerfRow>

      {/* WB Temperature — show-for-heatex (tmpl:437-455) */}
      {showHeatexWb && (
        <PerfRow label={<span className="required">WB Temperature</span>}
          dataName={`oa_wb_temp_${sea}`} dataName2={`ra_wb_temp_${sea}`}>
          <div className="col-xs-6">
            <PerfNum name={`oa_wb_temp_${sea}`} unit="F" unitClass="left percent temp"
              tabIndex={perfTab(sea, 2)} />
          </div>
          <div className="col-xs-6">
            <PerfNum name={`ra_wb_temp_${sea}`} unitClass="left percent" inputClass="col-xs-10"
              tabIndex={perfTab(sea, 7)} />
          </div>
        </PerfRow>
      )}

      {/* WB Temp / Humidity toggle — show-for-wbrh-toggle (tmpl:456-503).
          Legacy's label holds TWO clickable `.temp-toggle` spans (the active
          one goes blue, common.css:15919-15927) and the input-part holds a
          `.wb-group` and an `.rh-group` row, one of them hidden
          (applyWbRhToggleState, js:1690-1698). React had a single
          "WB / RH" <button> and separate full-width rows.

          The label cell is the ONLY row whose legacy markup carries an inline
          style (tmpl:458 and :714, both `display: flex; gap: 8px`). `.right-row`
          is already flex from common.css:15912, so the operative half is the
          8px gap — without it the two spans render as "WB TempHumidity". */}
      {hasWbRhToggle && (
        <PerfRow rowClass="wbtemp-humidity-row"
          labelStyle={{ display: 'flex', gap: '8px' }} wrapRow={false}
          dataName={`oa_rh_${sea}`} dataName2={`ra_rh_${sea}`}
          label={(
            <>
              <span className={`temp-toggle${isWbTemp ? ' active' : ''}`} data-type="temp"
                onClick={() => { if (!isWbTemp) onWbRhToggle(); }}>WB Temp</span>
              <span className={`temp-toggle${isWbTemp ? '' : ' active'}`} data-type="humidity"
                onClick={() => { if (isWbTemp) onWbRhToggle(); }}>Humidity</span>
            </>
          )}>
          <>
            <div className="row wb-group" style={{ display: isWbTemp ? undefined : 'none' }}>
              <div className="col-xs-6">
                <PerfNum name={`oa_wb_temp_${sea}`} unit="F / %" unitClass="temp percent"
                  tabIndex={perfTab(sea, 2)} />
              </div>
              <div className="col-xs-6">
                <PerfNum name={`ra_wb_temp_${sea}`} unitClass="left unit"
                  tabIndex={perfTab(sea, 7)} />
              </div>
            </div>
            <div className="row rh-group" style={{ display: isWbTemp ? 'none' : undefined }}>
              <div className="col-xs-6">
                <PerfNum name={`oa_rh_${sea}`} unit="gr/lb" unitClass="left unit" formatter="integer"
                  tabIndex={perfTab(sea, 2)} />
              </div>
              <div className="col-xs-6">
                <PerfNum name={`ra_rh_${sea}`} unitClass="left unit" formatter="integer"
                  tabIndex={perfTab(sea, 7)} />
              </div>
            </div>
          </>
        </PerfRow>
      )}

      {/* Air Pressure — oa_sp/ra_sp, NOT season-suffixed (tmpl:527-548).
          The template's literal `required` is dropped here on purpose: `oa_sp`
          is an `EW_REQUIRED_60` member, so `ewLabelIsRequired` re-adds it for
          every manufacturer legacy marks it for — and DROPS it for Semo (95),
          whose set omits oa_sp/ra_sp, exactly as legacy's loop does. */}
      <PerfRow label="Air Pressure" dataName="oa_sp" dataName2="ra_sp">
        <div className="col-xs-6">
          <PerfNum name="oa_sp" unit="in WC" unitClass="unit left" inputClass="winterToSummer"
            tabIndex={perfTab(sea, 4)} />
        </div>
        <div className="col-xs-6">
          <PerfNum name="ra_sp" unitClass="unit left" inputClass="winterToSummer"
            tabIndex={perfTab(sea, 9)} />
        </div>
      </PerfRow>

      {/* Pressure Difference — single half (tmpl:550-564) */}
      <PerfRow rowClass="top-margin" label="Pressure Difference"
        dataName={`difference_in_pressure_${sea}`}>
        <div className="col-xs-6">
          <PerfNum name={`difference_in_pressure_${sea}`} unit="in WC" unitClass="percent left"
            inputClass="winterToSummer" tabIndex={perfTab(sea, 10)} />
        </div>
      </PerfRow>

      {/* EATR-OACF checkbox — force-checked + non-clickable for Heatex-E
          (tmpl:566-580). */}
      <PerfRow rowClass={`eatr_oacf_div${isHeatexE ? ' non-clickable' : ''}`}
        label="EATR-OACF" dataName="eatr_oacf" inputPart={false}>
        <div className="col-xs-6">
          <span className="percent left" />
          <input type="checkbox" style={{ marginTop: 6, marginLeft: -10 }}
            name="eatr_oacf" data-name="eatr_oacf" className="search_field winterToSummer"
            /* tmpl:574 AND :830 — 22 in both seasons, legacy's own copy-paste. */
            tabIndex={22}
            checked={isHeatexE || Boolean(s.eatr_oacf)} disabled={isHeatexE}
            onChange={(e) => onCheckboxField('eatr_oacf', e.target.checked)} />
        </div>
      </PerfRow>

      {/* Velocity Range — two inputs in ONE half (tmpl:581-602), hide-for-annexair */}
      {!isAnnexair && (
        <PerfRow label="Velocity Range"
          dataName="min_face_velocity" dataName2="max_face_velocity">
          <div className="col-xs-6 four-input-row">
            <span className="percent">fpm</span>
            <input type="text" name="min_face_velocity" data-name="min_face_velocity"
              data-formatter_type="integer" autoComplete="off"
              className="minMaxFaceVel winterToSummer search_field model_field"
              tabIndex={perfTab(sea, 11)}
              {...freeTypingProps(formatIntegerDisplay(s.min_face_velocity),
                (raw) => onSearchField('min_face_velocity', raw),
                (raw) => formatIntegerDisplay(num(raw)))} />
            <input type="text" name="max_face_velocity" data-name="max_face_velocity"
              data-formatter_type="integer" autoComplete="off"
              className="minMaxFaceVel right winterToSummer search_field model_field"
              tabIndex={perfTab(sea, 12)}
              {...freeTypingProps(formatIntegerDisplay(s.max_face_velocity),
                (raw) => onSearchField('max_face_velocity', raw),
                (raw) => formatIntegerDisplay(num(raw)))} />
          </div>
        </PerfRow>
      )}

      {/* Altitude — readonly project elevation (tmpl:603-615), hide-for-annexair */}
      {!isAnnexair && (
        <PerfRow label="Altitude" dataName="altitude">
          <div className="col-xs-6">
            <PerfNum name="elevation" unit="ft" formatter="integer" model={false} readOnly />
          </div>
        </PerfRow>
      )}
    </div>
  );
};

/**
 * One legacy input row.
 *
 * ★ PARITY (ewheel.selector.tmpl) — React had hard-coded `col-xs-5` / `col-xs-7`
 * for every row and dropped the `input-part > .row > .col-xs-12` wrapper legacy
 * puts around each control. The legacy widths are NOT uniform:
 *   Properties / Mounting / Options / Pricing → 6 / 6   (tmpl:33-44, 955-1018,
 *                                                        1044-1150, 1158-1170)
 *   Configuration                            → 5 / 7   (tmpl:879-925)
 *   Performance                              → 4 / 8   (tmpl:377-615, handled by
 *                                                        PerfRow below)
 * so the whole dialog rendered with the wrong label/control split.
 *
 * ★ PARITY — nor is the wrapper INSIDE `.input-part` uniform, and the port used
 * `.row > .col-xs-12` everywhere. The three shapes legacy actually uses:
 *   `col12` — `.row > .col-xs-12 > ctl`             Properties / Configuration /
 *                                                    Mounting (tmpl:955-1018)
 *   `bare`  — `.row > ctl`, no column at all        Options   (tmpl:1044-1150)
 *   `right` — `.row > .col-xs-12.right.text-right`  Pricing   (tmpl:1163-1168)
 * They are not cosmetic: `.rotary-htex #er-wheel select` is
 * `width: calc(100% - 20px)` (common.css:19452), so the wrapper IS the select's
 * containing block. `bare` lets the select span the `.row`'s full −15px-margin
 * width — dev's Options selects are 15px wider than its Mounting ones — and
 * `right` (`.right` = `float:right!important`, :84) right-aligns the Method
 * select, ending 15px in from the left where the port started it flush.
 */
const SelectRow = ({ label, dataName, unit, cols = 6, rowClass, wrap = 'col12', children }: {
  label: string; dataName: string; unit?: string; cols?: 4 | 5 | 6;
  rowClass?: string; wrap?: 'col12' | 'bare' | 'right'; children: React.ReactNode;
}) => (
  <div className={rowClass ? `row ${rowClass}` : 'row'}>
    <div className={`col-xs-${cols} label-part`}>
      <span data-name={dataName}>{label}</span>
      {unit && <span className="percent right">{unit}</span>}
    </div>
    <div className={`col-xs-${12 - cols} input-part`}>
      <div className="row">
        {wrap === 'bare' ? children : (
          <div className={wrap === 'right' ? 'col-xs-12 right text-right' : 'col-xs-12'}>
            {children}
          </div>
        )}
      </div>
    </div>
  </div>
);

/**
 * Free-typing props for every numeric text input in this dialog.
 *
 * Legacy commits a numeric input on CHANGE, never on input: formatter.js:190-191
 * binds `element.onchange` with the `element.oninput` line deliberately commented
 * out ("Don't doing anything while editing"), and the selector's own bindings are
 * `change .model_field` / `change .search_field`
 * (energy_wheel.selector.js:286-287) — i.e. blur or Enter. So legacy never
 * rewrites the box while the caret is in it.
 *
 * The port had these CONTROLLED (`value={formatDecimalDisplay(...)}`) with an
 * onChange that wrote the state ref and called `bump()`. That broke typing twice
 * over:
 *   1. every keystroke re-rendered the selector, and `PerfNum` / `SeasonBlock`
 *      were defined INSIDE that render — a new function identity per render, so
 *      React saw a different element type, unmounted the `<input>` and mounted a
 *      fresh one. Focus was lost after the first character. (Those sub-views now
 *      live at module scope; see the `EwViewCtx` note above for why.)
 *   2. the value was re-formatted mid-edit, so typing "154" resolved to "1.00".
 *
 * Uncontrolled + commit-on-blur/Enter is what legacy does and fixes both. Same
 * shape as the DrawingWindow rewrite. (`CoilSelector`'s `NumControl` used to be
 * the other example; since #2062 it is CONTROLLED with a focus draft —
 * `CoilNumberBox` — because hoisting its controls to module scope took away the
 * remount those boxes had been relying on to re-read a committed or derived
 * value.) The one
 * legacy path not mirrored is its 1000 ms `_.debounce` keyup commit (js:518) —
 * that would re-render mid-typing, and it only ever commits the same value the
 * blur commits a moment later.
 *
 * An UNCHANGED value must not commit: a plain focus/blur pass would otherwise
 * `markChanged()` and drop the rated outputs.
 *
 * `reformat` re-encodes the box from what was just typed. React does not push a
 * changed `defaultValue` into an input the user has already edited, so with the
 * remount gone something has to put the commas and decimals back — which is
 * exactly what legacy's own `onchange` does
 * (`element.value = this.encode(this.intoRange(value), …)`, formatter.js:193).
 * It must mirror how the field's commit handler STORES the value, or the box and
 * the next render disagree.
 */
function freeTypingProps(
  display: string,
  commit: (raw: string) => void,
  reformat?: (raw: string) => string,
) {
  return {
    defaultValue: display,
    onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      if (raw !== display) {
        if (reformat) e.target.value = reformat(raw);
        commit(raw);
      }
    },
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') e.currentTarget.blur();
    },
  };
}

export default EnergyWheelSelector;
